import {
	type Action,
	createActionsHook,
	createContainer,
	createHook,
	type StoreActionApi,
} from 'react-sweet-state';

import { type DocNode } from '@atlaskit/adf-schema';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';
import { getRovoParams, updatePageRovoParams } from '@atlaskit/rovo-triggers/params';
import type { RovoApp } from '@atlassian/conversation-assistant-chat-prompt-input/controllers/chat-mode-store';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import {
	type FollowUpSource,
	type InteractionSource,
} from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import {
	ExperienceName,
	type ExperienceNameType,
	sendMessageExperienceName,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	addSentryBreadcrumb,
	logException,
} from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { ASSISTANCE_SERVICE_ERROR_CODES } from '@atlassian/conversation-assistant-service-api/constants';
import {
	type AgentVersionInput,
	type AlphaExtendedThinkingMode,
	type AssistantMessage,
	type ChatMessage,
	type Dialogues,
	type FetchStreamReturn,
	isActionExecutedSuccessfully,
	isAssistantMessage,
	isChannelNotFoundError,
	isConversationChannelDataMessage,
	isFollowUpMessage,
	isHumanMessage,
	isInProgressStreamResponse,
	isSSEMessage,
	isStreamAnswerPart,
	isStreamAUPError,
	isStreamError,
	isStreamReconnectSupportPart,
	isStreamResponse,
	isStreamTaskStatus,
	isStreamTodoSnapshot,
	isStreamTrace,
	type LastAgentMessageStatus,
	type MessageFileId,
	type MessageType,
	type ParsedSSEMessage,
	type RovoLabsConfirmedTools,
	type StreamMessage,
	type StreamResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type {
	MessageActionInvocationStatus,
	MessageActionPayload,
} from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { generateConversationName } from '@atlassian/conversation-assistant-service/services/assistance-service/utils/conversation';
import {
	createChatStore,
	EMPTY_CONVERSATION_ID,
} from '@atlassian/conversation-assistant-store/controllers/chat';
import {
	type AgentMetadata,
	type AgentVersion,
	AnswerNowStatusType,
	CHAT_STORE_STATUS,
	type ChatStorePrivateState,
	type Conversation,
	isAnswerNowAvailable,
	isStoreAssistant,
	isStoreError,
	isStoreFollowUp,
	isStoreHuman,
	StoreActionLoggableName,
	type StoreChatItem,
	type TaskProgressState,
	type TodoSnapshot,
	isTerminalTaskState,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import {
	getChatLoadPreferenceKey,
	SHORTER_TIMEOUT_BEFORE_NEW_CHAT,
} from '../../common/constants/private';
import { StoreErrorFlagId } from '../../common/ui/hooks/use-show-store-error-flag';
import { interactionMetrics } from '../../common/utils/browser-metrics';
import { randomIntFromInterval } from '../../common/utils/id-generator';
import { isRovoCustomAgent } from '../../common/utils/is-rovo-custom-agent';
import { getLastMessageSentAt, setLastMessageSentAt } from '../last-message-sent';

import { MessageActionError, NoFinalResponseError, StreamErrorException } from './error-classes';
import { handleStreamTaskStatus } from './handle-stream-task-status';
import { mergeConversationWithNetworkPriority } from './mergeConversationWithNetworkPriority';
import {
	type ReconnectToInProgressStreamParams,
	reconnectToInProgressStreamProvider,
} from './reconnect-action';
import { createChatReconnectableStream } from './reconnectable-stream';
import {
	processResearchStreamMessage,
	sendMessageActionProvider,
	type SendMessageConsumerPramas,
} from './send-message-action';
import { createStoreAbortController } from './store-abort-controller';
import {
	type ActionErrorHandler,
	type OnErrorCallback,
	storeErrorHandler,
} from './store-error-handler';
import type {
	ChatStoreAction,
	ChatStoreContainerProps,
	ChatStoreState,
	MessageSource,
	ResearchStreamState,
} from './types';
import {
	addAnswerPart,
	addAnswerPartWithMarkdownData,
	checkActiveConversationMatchesSource,
	clearStatus,
	clearStoreAssistantMessageContent,
	clearTracesIfNotEmpty,
	createStreamMarkdownProcessor,
	dedupeTraceMessagesWithLatest,
	createStoreAssistant,
	createStoreAssistantWithContent,
	createStoreError,
	createStoreHuman,
	createStoreHumanWithContent,
	deriveInvocationStatus,
	extractMarkdownContent,
	findStoreMessageIndex,
	generateId,
	generateTmpMessageId,
	getAgentMessageById,
	getAssociatedHumanMessageId,
	getConversation,
	getLastAgentMessageID,
	getSourceAgentId,
	getTraceFromStreamMessage,
	isEmptyConversation,
	isValidAdfJsonString,
	markStreamingMessagesAsComplete,
	processStreamResponseMarkdown,
	replaceAnswerData,
	updateStoreAssistantMessageAnswerNowStatus,
	updateStoreAssistantMessageId,
} from './utils';
/**
 * Util action to initialize conversation with conversationId
 */
const initializeConversationData =
	({
		conversationId,
		data: conversationData,
	}: {
		conversationId: string;
		data: Conversation;
	}): ChatStoreAction =>
	({ getState, setState }) => {
		setState({
			conversations: {
				...getState().conversations,
				[conversationId]: {
					...(getState().conversations[conversationId] ?? {}),
					...conversationData,
				},
			},
		});
	};

/**
 * Util action to allow updating the conversation based on `conversationId`
 * without needing to keep spreading the existing
 *
 * Similar to `initializeConversationData`, the difference is, it will not allow the the data to be updated if there is no existing conversation
 */
const setConversationData =
	({
		conversationId,
		data: conversationData,
	}: {
		conversationId: string;
		data: Partial<Conversation>;
	}): ChatStoreAction =>
	({ getState, setState }) => {
		if (!getState().conversations[conversationId]) {
			return;
		}

		setState({
			conversations: {
				...getState().conversations,
				[conversationId]: {
					...(getState().conversations[conversationId] ?? {}),
					...conversationData,
				},
			},
		});
	};

/**
 * Utils function to remove conversation from store
 */
const removeConversationFromStore =
	({ conversationId }: { conversationId: string }): ChatStoreAction =>
	({ getState, setState }) => {
		const { [conversationId]: _, ...otherConversations } = getState().conversations;
		setState({
			conversations: otherConversations,
		});
	};

/**
 * Action Factory
 * Adds custom _middlewares_ to handle common cases and unexpected errors.
 * This is a hack to attach common logic to all actions.
 * It should not use the store action dispatcher. Instead, should call the actionCallback directly.
 *
 *
 * To add invocation-level error handling (`onError`):
 *
 * 1. Define actionargs type with `onError` using `OnErrorCallback`:
 *    ```ts
 *    type MyActionArgs = {
 *      myParam: string;
 *      onError?: OnErrorCallback<MyActionArgs>;
 *    };
 *    ```
 *
 * 2. Wire up `actionErrorHandler` to delegate to `onError`:
 *    ```ts
 *    createSafeAction(name, actionFn, {
 *      actionErrorHandler: (context) => {
 *        const { onError, ...args } = context.args?.[0] ?? {};
 *			if (onError) {
 *				return onError({ ...context, args });
 *			}
 *			// default action level error handling
 *			return { proceedToDefaultHandling: true };
 *      },
 *    });
 *    ```
 *
 */
export const createSafeAction =
	<T extends (...args: any[]) => any, R = void>(
		actionLoggableName: StoreActionLoggableName,
		actionHOF: T,
		props?: {
			experienceName?: (args: Parameters<T>) => ExperienceNameType;
			getRateLimitState?: (args: Parameters<T>) => { isDeepResearchAttempt?: boolean };
			actionErrorHandler?: ActionErrorHandler<Parameters<T>>;
		},
	) =>
	(...args: Parameters<T>): Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<R>> =>
	async (api: StoreActionApi<ChatStorePrivateState>, containerProps: ChatStoreContainerProps) => {
		/*
		  Add custom logic here
		 */
		// General try/catch statement to handle unexpected errors
		try {
			addSentryBreadcrumb(`createSafeAction: executing ${actionLoggableName}`);
			// Here we call the action callback
			// Same as dispatch(actionHOF)
			const action = actionHOF(...args);
			// Here we execute the action i.e. what happens internally when the action is dispatched
			const result = await action(api, containerProps);
			return result as R;
		} catch (error: unknown) {
			storeErrorHandler({
				args,
				api,
				actionLoggableName,
				error,
				containerProps,
				setConversationData,
				experienceName: props?.experienceName,
				getRateLimitState: props?.getRateLimitState,
				actionErrorHandler: props?.actionErrorHandler,
			});
			return undefined as R;
		}
	};

/**
 * ************************************************************
 * Private Actions
 * ************************************************************
 */

const addStoreChatItem = createSafeAction(
	StoreActionLoggableName.addStoreChatItem,
	({
		message,
		conversationId: conversationIdParam,
	}: {
		message: StoreChatItem;
		conversationId?: string;
	}): ChatStoreAction =>
		({ getState }, { setMessages, getMessages }) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;
			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return;
			}

			setMessages(conversationId, [...getMessages(conversationId), message]);
		},
);

const addHumanMessage = createSafeAction(
	StoreActionLoggableName.addHumanMessage,
	({
		id,
		content,
		conversationId: conversationIdParam,
		isFromSeededConversation = false,
		requestId,
		fileUploads,
	}: {
		id: string;
		content: DocNode;
		conversationId?: string;
		isFromSeededConversation?: boolean;
		requestId?: string;
		fileUploads?: MessageFileId[];
	}): ChatStoreAction =>
		({ getState, dispatch }) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;
			dispatch(
				addStoreChatItem({
					message: createStoreHuman(id, content, isFromSeededConversation, requestId, fileUploads),
					conversationId,
				}),
			);
		},
);

const addAssistantMessage = createSafeAction(
	StoreActionLoggableName.addAssistantMessage,
	({
		id,
		content,
		conversationId: conversationIdParam,
		agentNamedId,
	}: {
		id: string;
		content: string | DocNode;
		conversationId?: string;
		agentNamedId?: string;
	}): ChatStoreAction =>
		({ getState, dispatch }) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;

			dispatch(
				addStoreChatItem({
					message: createStoreAssistant({ id, content, agentNamedId, conversationId }),
					conversationId,
				}),
			);
		},
);

/**
 * ************************************************************
 * Public Actions
 * ************************************************************
 */

const clearFollowUpMessages = createSafeAction(
	StoreActionLoggableName.clearFollowUpMessages,
	({ conversationId: conversationIdParam }: { conversationId?: string }): ChatStoreAction =>
		({ getState }, { getMessages, setMessages }) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;

			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return;
			}

			const messages = getMessages(conversationId);
			const filteredMessages = messages.filter((message) => !isStoreFollowUp(message));

			setMessages(conversationId, filteredMessages);
		},
);

/**
 * Send a message to the assistant and add it to react-sweet-state
 *
 * @param agentId -- this parameter will be prioritised over agent.id
 */
const sendMessage = createSafeAction(
	StoreActionLoggableName.sendMessage,
	(props: SendMessageConsumerPramas): ChatStoreAction =>
		sendMessageActionProvider({
			...props,
			setConversationData,
			createConversationOptimistically,
			addHumanMessage,
			addAssistantMessage,
			clearFollowUpMessages,
			setSelectedConversation,
			reconnectToInProgressStream,
			createTaskFromStream,
			setTaskProgressState,
			setTaskTodoSnapshot,
		}),
	{
		experienceName: (args) =>
			sendMessageExperienceName({
				deepResearchEnabled: args[0]?.deepResearchEnabled,
				thinkDeeperEnabled: args[0]?.thinkDeeperEnabled,
				fastModeEnabled: args[0]?.fastModeEnabled,
			}),
		getRateLimitState: (args) => ({
			isDeepResearchAttempt: args[0]?.deepResearchEnabled,
		}),
	},
);

/**
 * Similar action to `sendMessage` with the barebone functionalities to confirm tools/skills/actions
 *
 * Initially this will assume that this function only created on existing conversation
 */
const sendMessageConfirmedTools = createSafeAction(
	StoreActionLoggableName.sendMessageConfirmedTools,
	({
		conversationId: conversationIdParam,
		agentId,
		confirmed_tools,
		messageSources,
		storeUserMessages = false,
		webSearchEnabled,
		deepResearchEnabled,
		thinkDeeperEnabled,
		alphaExtendedThinkingMode,
		fastModeEnabled,
		taskModeEnabled,
		includeAIFeatureContext,
		agentVersion,
	}: {
		conversationId?: string;
		agentId?: string;
		confirmed_tools: RovoLabsConfirmedTools;
		messageSources?: Partial<MessageSource>;
		storeUserMessages?: boolean;
		webSearchEnabled?: boolean;
		deepResearchEnabled?: boolean;
		thinkDeeperEnabled?: boolean;
		alphaExtendedThinkingMode?: AlphaExtendedThinkingMode;
		fastModeEnabled?: boolean;
		taskModeEnabled?: boolean;
		includeAIFeatureContext?: boolean;
		agentVersion?: AgentVersionInput;
	}): ChatStoreAction =>
		async (
			{ getState, dispatch },
			{
				assistanceService,
				chatContextStoreActions,
				experienceTracker,
				getMessages,
				setMessages,
				trackAIMAUEvent,
				trackAgentEvent,
				processStreamResearch,
				addImplicitResearchPlan,
				resetHasReceivedAnswerPart,
				clearMessageResearch,
				updateResearchMessageId,
				markIncompleteResearchesErrored,
				sendAnalyticsEvent,
				getIsChatMounted,
			},
		) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;
			const resolvedAgentVersion =
				agentVersion ?? getState().conversations[conversationId]?.agentVersion;

			// Collection of callbacks for experience and performance tracking
			const expAndPerfTrackers = {
				start: () => {
					experienceTracker.start({
						name: ExperienceName.SEND_CONFIRMED_TOOLS_MESSAGE,
						id: conversationId,
						attributes: {
							conversationId,
							interactionSource: messageSources?.interactionSource,
						},
					});
					interactionMetrics.sendConfirmedToolsTimeToFinishStream.start();
					interactionMetrics.sendConfirmedToolsTimeToFirstAnswerPart.start();
				},
				firstAnswerPartReceived: () => {
					interactionMetrics.sendConfirmedToolsTimeToFirstAnswerPart.stop();
				},
				succeed: () => {
					experienceTracker.succeed({
						name: ExperienceName.SEND_CONFIRMED_TOOLS_MESSAGE,
					});
					interactionMetrics.sendConfirmedToolsTimeToFinishStream.stop();
				},
				aupError: () => {
					experienceTracker.succeed({
						name: ExperienceName.SEND_CONFIRMED_TOOLS_MESSAGE,
					});
					interactionMetrics.sendConfirmedToolsTimeToFinishStream.stop();
					interactionMetrics.sendConfirmedToolsTimeToFirstAnswerPart.stop();
				},
				agentDeactivated: ({ error }: { error: ApiError }) => {
					experienceTracker.fail({
						name: ExperienceName.SEND_CONFIRMED_TOOLS_MESSAGE,
						error,
					});
					interactionMetrics.sendConfirmedToolsTimeToFinishStream.cancel();
					interactionMetrics.sendConfirmedToolsTimeToFirstAnswerPart.cancel();
				},
				abort: () => {
					experienceTracker.abort({
						name: ExperienceName.SEND_CONFIRMED_TOOLS_MESSAGE,
						reason: 'Aborted',
					});
					interactionMetrics.sendConfirmedToolsTimeToFinishStream.cancel();
					interactionMetrics.sendConfirmedToolsTimeToFirstAnswerPart.cancel();
				},
				fail: ({ error }: { error: Error }) => {
					experienceTracker.fail({
						name: ExperienceName.SEND_CONFIRMED_TOOLS_MESSAGE,
						error: error,
					});
					interactionMetrics.sendConfirmedToolsTimeToFinishStream.stop();
					interactionMetrics.sendConfirmedToolsTimeToFirstAnswerPart.stop();
				},
			};

			const trackingAttributes: {
				requestId?: string;
				messageId?: string;
				humanMessageId?: string;
				instrumentationId?: string;
				initiatedTimestamp?: string;
				isRovoCustomAgent: boolean | undefined;
				sendMessageReason: 'TOOLS_CONFIRMATION';
				scenarioId?: string;
			} = {
				isRovoCustomAgent: isRovoCustomAgent(agentId),
				sendMessageReason: 'TOOLS_CONFIRMATION',
			};

			const agentAnalyticsAttributes: {
				touchPoint: string;
				tools: {
					toolId: string;
					toolSource: string;
					resolutionType: string;
				}[];
			} = {
				touchPoint: 'chatSendMessageConfirmedTools',
				tools: confirmed_tools.tools.map((tool) => ({
					toolId: tool.tool_id ?? 'UNKNOWN_TOOL_ID',
					toolSource: tool.tool_source ?? 'UNKNOWN_TOOL_SOURCE',
					resolutionType: tool.resolution?.type ?? 'UNKNOWN_RESOLUTION_TYPE',
				})),
			};

			// Collection of callbacks for analytics tracking
			const analyticsTrackers = {
				/**
				 * There are 2 AIMAU events here, because in resumption we're doing 2 things
				 * 1. We're confirming the previous tools result
				 * 2. And the confirmation triggers a LLM chat streaming
				 */
				initiated: ({ timestamp }: { timestamp?: string }) => {
					trackAIMAUEvent({
						...trackingAttributes,
						type: 'actioned',
						actionResult: 'toolsExecutionConfirmed',
						proactive: false,
						singleInstrumentationID: trackingAttributes.instrumentationId,
					});

					trackAIMAUEvent({
						...trackingAttributes,
						type: 'initiated',
						subjectId: messageSources?.interactionSource,
						proactive: false,
						userGenerated: true,
						singleInstrumentationID: trackingAttributes.instrumentationId,
						timestamp,
					});

					if (agentId && isRovoCustomAgent(agentId)) {
						trackAgentEvent({
							action: 'toolsExecutionConfirmed',
							actionSubject: 'rovoAgent',
							attributes: {
								...trackingAttributes,
								...agentAnalyticsAttributes,
								agentId,
								singleInstrumentationId: trackingAttributes.instrumentationId,
							},
						});
					}
				},
				aborted: () => {
					trackAIMAUEvent({
						...trackingAttributes,
						type: 'dismissed',
						subjectId: messageSources?.interactionSource,
						proactive: false,
						userGenerated: true,
						messageId: trackingAttributes.messageId,
						singleInstrumentationID: trackingAttributes.instrumentationId,
					});

					if (agentId && isRovoCustomAgent(agentId)) {
						trackAgentEvent({
							action: 'toolsExecutionStreamStopped',
							actionSubject: 'rovoAgent',
							attributes: {
								...trackingAttributes,
								...agentAnalyticsAttributes,
								agentId,
								singleInstrumentationId: trackingAttributes.instrumentationId,
							},
						});
					}
				},
				viewed: () => {
					trackAIMAUEvent({
						...trackingAttributes,
						type: 'viewed',
						proactive: false,
						userGenerated: true,
						source: messageSources?.interactionSource,
						messageId: trackingAttributes.messageId,
						subjectId: messageSources?.followUpSource,
						citedProductList: {},
						sourceProducts: {},
						singleInstrumentationID: trackingAttributes.instrumentationId,
						timestamp: new Date().toISOString(),
					});

					if (agentId && isRovoCustomAgent(agentId)) {
						trackAgentEvent({
							action: 'toolsExecutionResultViewed',
							actionSubject: 'rovoAgent',
							attributes: {
								...trackingAttributes,
								...agentAnalyticsAttributes,
								agentId,
								singleInstrumentationId: trackingAttributes.instrumentationId,
								scenarioId: trackingAttributes.scenarioId,
							},
						});
					}
				},
				error: () => {
					trackAIMAUEvent({
						...trackingAttributes,
						type: 'error',
						proactive: false,
						userGenerated: true,
						messageId: trackingAttributes.requestId,
						singleInstrumentationID: trackingAttributes.instrumentationId,
						timestamp: new Date().toISOString(),
					});

					if (agentId && isRovoCustomAgent(agentId)) {
						trackAgentEvent({
							action: 'toolsExecutionResultError',
							actionSubject: 'rovoAgent',
							attributes: {
								...trackingAttributes,
								...agentAnalyticsAttributes,
								agentId,
								singleInstrumentationId: trackingAttributes.instrumentationId,
							},
						});
					}
				},
			};

			const updateCurrentConversation = (data: Partial<Conversation>) => {
				dispatch(
					setConversationData({
						conversationId,
						data,
					}),
				);
			};

			const { abortController, storeAbortController, confirmAbort } = createStoreAbortController();

			let hasReceivedFinalResponse = false;
			let generator: AsyncGenerator<StreamMessage | ParsedSSEMessage<StreamMessage>> | undefined;
			let responseData: FetchStreamReturn<StreamMessage, boolean>['responseData'] | undefined;
			let hasLoggedInitiatedEvent = false;
			// Hoisted so it is accessible in the catch block for clearMessageResearch
			let tmpAssistantMessageId = generateTmpMessageId(conversationId);
			// Reconnect state — populated once we receive the conversation channel data message
			let agentMessageId: string | undefined;
			let isReconnectSupported = false;

			const handleAbort = () => {
				expAndPerfTrackers.abort();
				confirmAbort();
				clearMessageResearch({ messageId: tmpAssistantMessageId });
				!hasLoggedInitiatedEvent &&
					analyticsTrackers.initiated({ timestamp: trackingAttributes.initiatedTimestamp });
				analyticsTrackers.aborted();

				// Call the cancel API to stop backend processing
				if (
					trackingAttributes.messageId &&
					trackingAttributes.messageId !== tmpAssistantMessageId
				) {
					assistanceService
						.cancelMessageStream({
							conversationId,
							messageId: trackingAttributes.messageId,
						})
						.catch(() => {
							// Log but don't throw - the abort is already happening
							addSentryBreadcrumb({
								message: 'Failed to cancel message stream after abort in sendMessageConfirmedTools',
								data: {
									conversationId,
									messageId: trackingAttributes.messageId,
								},
							});
						});
				}
			};

			try {
				expAndPerfTrackers.start();

				// change the conversation status
				updateCurrentConversation({
					storeAbortController,
					status: CHAT_STORE_STATUS.awaitingResponse,
					lastAgentMessageStatus: 'IN_PROGRESS',
				});

				// Only add assistant message
				dispatch(
					addAssistantMessage({
						id: tmpAssistantMessageId,
						content: '',
						conversationId,
						agentNamedId: agentId,
					}),
				);
				dispatch(clearFollowUpMessages({ conversationId }));
				trackingAttributes.messageId = tmpAssistantMessageId;
				trackingAttributes.instrumentationId = tmpAssistantMessageId;

				// Assumed the insert is successful and inserts at the last position
				const answerIndex = getMessages(conversationId).length - 1;

				// Call the BE endpoint
				trackingAttributes.initiatedTimestamp = new Date().toISOString();
				const response = await assistanceService.sendMessageConfirmedTools({
					conversationId,
					agentId,
					confirmed_tools,
					controller: abortController,
					optInSSEStream: true,
					storeMessage: storeUserMessages,
					webSearchEnabled,
					deepResearchEnabled,
					thinkDeeperEnabled,
					alphaExtendedThinkingMode,
					fastModeEnabled,
					...(taskModeEnabled ? { planModeEnabled: taskModeEnabled } : {}),
					...(includeAIFeatureContext && {
						aiFeatureContext: chatContextStoreActions.getAIFeatureContext(),
					}),
					...(resolvedAgentVersion ? { agentVersion: resolvedAgentVersion } : {}),
					optInToDeepResearchV2: true,
					optInThinkDeeperMessages: FeatureGates.getExperimentValue(
						'rovo_chat_thinking_deeper_messages_experiment',
						'enabled',
						false,
					),
					optInAnsNow: fg('rovo_chat_enable_ans_now'),
					optInNewToolTraces: FeatureGates.getExperimentValue(
						'rovo_chat_thinking_traces_improvements',
						'isEnabled',
						false,
						{ fireExperimentExposure: false },
					),
					optInNewTeamworkTraceIcons: fg('rovo_chat_enable_twg_trace_team26'),
				});
				generator = response.generator;
				responseData = response.responseData;

				// Update action invocation statuses based on the confirmed_tools payload
				if (fg('rovo_chat_enable_tasks')) {
					dispatch(
						updateConfirmedToolsActionStatuses({
							conversationId,
							messageId: confirmed_tools.message_id,
							tools: confirmed_tools.tools,
							status: 'fromResolution',
						}),
					);
				}

				/**
				 * --------
				 * Stream processing START
				 * --------
				 */

				updateCurrentConversation({
					status: CHAT_STORE_STATUS.typingResponse,
				});

				// Research store state shared across stream messages
				const researchState: ResearchStreamState = {
					clientRoundCounter: 0,
					hasAccumulatedAnswerContent: false,
				};

				const processStreamMarkdown = createStreamMarkdownProcessor();

				const streamToIterate = FeatureGates.getExperimentValue(
					'rovo_chat_reconnect_all_experiment',
					'isEnabled',
					false,
				)
					? createChatReconnectableStream({
							generator,
							conversationId,
							abortController,
							assistanceService,
							sendAnalyticsEvent,
							experienceTracker,
							getMessageId: () => agentMessageId,
							canReconnect: () => !!(agentMessageId && isReconnectSupported),
							updateCurrentConversation,
							getIsChatMounted,
							analyticsAttributes: {
								isDeepResearch: deepResearchEnabled,
								thinkDeeperEnabled,
								fastModeEnabled,
							},
							onBeforeFirstReconnect: () => {
								if (agentMessageId) {
									const chat = getMessages(conversationId);
									const alreadySwapped = findStoreMessageIndex(chat, agentMessageId) >= 0;

									if (!alreadySwapped) {
										// Swap the tmp message ID → real agent message ID in the
										// message store so addAnswerPart/replaceAnswerData ID
										// validation doesn't throw on the replayed stream chunks.
										setMessages(
											conversationId,
											updateStoreAssistantMessageId({
												chat,
												oldId: tmpAssistantMessageId,
												newId: agentMessageId,
											}),
										);
										updateResearchMessageId({
											messageId: tmpAssistantMessageId,
											newMessageId: agentMessageId,
										});
									}

									// Reassign so subsequent processResearchStreamMessage calls
									// and addAnswerPart/replaceAnswerData use the real agent
									// message ID as the key.
									tmpAssistantMessageId = agentMessageId;
								}
							},
						})
					: generator;

				for await (const streamObj of streamToIterate) {
					const streamMessage = isSSEMessage(streamObj) ? streamObj.data : streamObj;

					/**
					 * Research store processing — handles think-deeper traces, implicit round
					 * detection, and research plan updates for all message types.
					 */
					processResearchStreamMessage({
						streamMessage,
						tmpAssistantMessageId,
						conversationId,
						deepResearchEnabled,
						getMessages,
						setMessages,
						processStreamResearch,
						addImplicitResearchPlan,
						resetHasReceivedAnswerPart,
						researchState,
					});

					/**
					 * Conversation channel data processing (usually the first stream part)
					 */
					if (isConversationChannelDataMessage(streamMessage)) {
						agentMessageId = streamMessage.assistant_message_id;
						trackingAttributes.instrumentationId = agentMessageId;
						trackingAttributes.messageId = agentMessageId;
						analyticsTrackers.initiated({ timestamp: trackingAttributes.initiatedTimestamp });
						hasLoggedInitiatedEvent = true;
						// Update the research store with the real message ID once we have it
						updateResearchMessageId({
							messageId: tmpAssistantMessageId,
							newMessageId: agentMessageId,
						});
					}

					/**
					 * Reconnect support detection
					 */
					if (isStreamReconnectSupportPart(streamMessage)) {
						isReconnectSupported = true;
					}

					/**
					 * Trace processing
					 */
					if (isStreamTrace(streamMessage)) {
						if (!trackingAttributes.requestId) {
							trackingAttributes.requestId = streamMessage.metadata?.request_id;
						}

						updateCurrentConversation({
							traces: dedupeTraceMessagesWithLatest([
								...(getState().conversations[conversationId]?.traces ?? []),
								getTraceFromStreamMessage(streamMessage),
							]),
						});
					}

					/**
					 * Answer part processing
					 */
					if (isStreamAnswerPart(streamMessage)) {
						expAndPerfTrackers.firstAnswerPartReceived();
						clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);

						let processedStreamMessage = streamMessage;
						let processedStreamResult = null;

						if (fg('rovo_enable_component_streaming')) {
							processedStreamResult = processStreamMarkdown(processedStreamMessage);
							processedStreamMessage = processedStreamResult.processedStreamMessage;
						}

						if (
							processedStreamResult &&
							processedStreamResult.markdownData &&
							fg('rovo_enable_component_streaming')
						) {
							setMessages(
								conversationId,
								addAnswerPartWithMarkdownData({
									chat: getMessages(conversationId),
									answerId: tmpAssistantMessageId,
									answerPart: processedStreamMessage,
									markdownData: processedStreamResult.markdownData,
									useIndex: answerIndex,
								}),
							);
						} else {
							setMessages(
								conversationId,
								addAnswerPart({
									chat: getMessages(conversationId),
									answerId: tmpAssistantMessageId,
									answerPart: processedStreamMessage,
									useIndex: answerIndex,
								}),
							);
						}
					}

					/**
					 * Final response processing
					 */
					if (isStreamResponse(streamMessage)) {
						hasReceivedFinalResponse = true;

						const humanMessageId = streamMessage?.message?.human_message?.id.toString();
						const finalAgentMessageId = streamMessage?.message?.message?.id.toString();

						clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);

						const { processedStreamMessage: processedFinalMessage, markdownData } =
							processStreamResponseMarkdown({
								streamMessage,
								messageId: finalAgentMessageId,
								conversationId,
								sendAnalyticsEvent,
							});

						setMessages(
							conversationId,
							replaceAnswerData({
								chat: getMessages(conversationId),
								answerId: tmpAssistantMessageId,
								content: processedFinalMessage,
								useIndex: answerIndex,
								humanMessageId,
								markdownData: Object.keys(markdownData).length > 0 ? markdownData : undefined,
							}),
						);

						// Update the research store key so rounds persist after the message ID changes
						if (finalAgentMessageId && finalAgentMessageId !== tmpAssistantMessageId) {
							updateResearchMessageId({
								messageId: tmpAssistantMessageId,
								newMessageId: finalAgentMessageId,
							});
						}

						trackingAttributes.humanMessageId = humanMessageId;
						trackingAttributes.scenarioId = streamMessage.message.message?.scenario?.id;
						if (!hasLoggedInitiatedEvent) {
							trackingAttributes.messageId = finalAgentMessageId;
							trackingAttributes.instrumentationId = finalAgentMessageId;
						}
					}

					/**
					 * Follow up processing
					 */
					if (isFollowUpMessage(streamMessage)) {
						const messages = getMessages(conversationId);

						clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);
						setMessages(conversationId, [
							...messages,
							{
								type: 'FOLLOW_UP',
								content: streamMessage,
								id: randomIntFromInterval(0, 1000000),
								agentMessageId: getLastAgentMessageID(messages),
								humanMessageId: trackingAttributes.humanMessageId,
							},
						]);
					}

					/**
					 * Task status processing
					 */
					if (isStreamTaskStatus(streamMessage)) {
						handleStreamTaskStatus({
							conversationId,
							taskMessage: streamMessage.message,
							messageId: trackingAttributes.messageId,
							createTaskFromStream,
							setTaskProgressState,
							dispatch,
						});
					}

					/**
					 * Todo snapshot processing
					 */
					if (isStreamTodoSnapshot(streamMessage)) {
						dispatch(
							setTaskTodoSnapshot({
								conversationId,
								todoSnapshot: { todos: streamMessage.message.todos },
							}),
						);
					}

					/**
					 * Error processing
					 */
					if (isStreamError(streamMessage)) {
						const { message, metadata, millisOffset, type } = streamMessage;

						// AUP violations are still considered successful
						if (isStreamAUPError(streamMessage)) {
							expAndPerfTrackers.aupError();
						}

						throw new StreamErrorException(message?.content || 'Unknown', {
							detail: {
								message,
								metadata,
								millisOffset,
								type,
							},
							traceId: response.responseData?.traceId,
						});
					}
				}
			} catch (error) {
				const isAgentDeactivated =
					error instanceof ApiError &&
					error.errorAttributes.errorCode?.includes(
						ASSISTANCE_SERVICE_ERROR_CODES.AGENT_DEACTIVATED,
					);
				const isNoAgentPermission =
					error instanceof ApiError &&
					error.errorAttributes.errorCode?.includes(
						ASSISTANCE_SERVICE_ERROR_CODES.AGENT_FORBIDDEN,
					) &&
					fg('convo-ai-enable-agents-use-permission');

				if (isAgentDeactivated || isNoAgentPermission) {
					expAndPerfTrackers.agentDeactivated({ error });

					updateCurrentConversation({
						status: CHAT_STORE_STATUS.deactivatedAgentError,
						traces: [],
						errorCode: fg('convo-ai-enable-agents-use-permission')
							? error.errorAttributes.errorCode
							: undefined,
					});

					// Mark all confirmed tool actions as errored
					if (fg('rovo_chat_enable_tasks')) {
						dispatch(
							updateConfirmedToolsActionStatuses({
								conversationId,
								messageId: confirmed_tools.message_id,
								tools: confirmed_tools.tools,
								status: 'errored',
							}),
						);
					}

					!hasLoggedInitiatedEvent &&
						analyticsTrackers.initiated({ timestamp: trackingAttributes.initiatedTimestamp });
					analyticsTrackers.error();
					return;
				}

				if (error instanceof Error && error.name === 'AbortError') {
					handleAbort();
					return;
				}

				// Mark all confirmed tool actions as errored
				if (fg('rovo_chat_enable_tasks')) {
					dispatch(
						updateConfirmedToolsActionStatuses({
							conversationId,
							messageId: confirmed_tools.message_id,
							tools: confirmed_tools.tools,
							status: 'errored',
						}),
					);
				}

				expAndPerfTrackers.fail({
					error: error instanceof Error ? error : new Error(String(error)),
				});
				markIncompleteResearchesErrored({ messageId: tmpAssistantMessageId });
				!hasLoggedInitiatedEvent &&
					analyticsTrackers.initiated({ timestamp: trackingAttributes.initiatedTimestamp });
				analyticsTrackers.error();
				throw error;
			} finally {
				updateCurrentConversation(clearStatus({}));
				setMessages(conversationId, markStreamingMessagesAsComplete(getMessages(conversationId)));
			}

			/**
			 * --------
			 * Stream processing END
			 * --------
			 */

			// Handle programmatic abort — the generator may exit cleanly without throwing AbortError
			// when the abort controller is signalled. Check the signal to detect this case.
			if (abortController.signal.aborted) {
				handleAbort();
				return;
			}

			// if stream finishes without FINAL_RESPONSE, BE is hanging
			if (!hasReceivedFinalResponse) {
				const error = new NoFinalResponseError(
					'The response took too long and timed out. Try refreshing the page.',
					{
						traceId: responseData?.traceId,
					},
				);
				experienceTracker.fail({
					name: ExperienceName.SEND_CONFIRMED_TOOLS_MESSAGE,
					error,
				});
				markIncompleteResearchesErrored({ messageId: tmpAssistantMessageId });
				!hasLoggedInitiatedEvent &&
					analyticsTrackers.initiated({ timestamp: trackingAttributes.initiatedTimestamp });
				analyticsTrackers.error();
				throw error;
			}

			!hasLoggedInitiatedEvent &&
				analyticsTrackers.initiated({ timestamp: trackingAttributes.initiatedTimestamp });
			analyticsTrackers.viewed();
			expAndPerfTrackers.succeed();
			if (fg('rovo_chat_chat_history_refresh')) {
				//If user is viewing a different conversation or not viewing ConversationAssitant, optimistically set conversation to unread
				if (getState().currentConversationId !== conversationId) {
					dispatch(
						setConversationData({
							conversationId,
							data: { readStatus: false },
						}),
					);
				}
			}
		},
);

const removeMessage = createSafeAction(
	StoreActionLoggableName.removeMessage,
	({
		messageId,
		conversationId: conversationIdParam,
	}: {
		messageId: number;
		conversationId?: string;
	}): ChatStoreAction =>
		async ({ getState }, { getMessages, setMessages }) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;

			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return;
			}

			const messages = getMessages(conversationId);
			const filteredMessages = messages.filter((message) => {
				if (isStoreError(message) || isStoreFollowUp(message)) {
					return message.id !== messageId;
				} else if (isStoreHuman(message)) {
					return message.content.id !== String(messageId);
				}

				// Catch-all to keep any other message type
				else {
					return true;
				}
			});

			setMessages(conversationId, filteredMessages);
		},
);

/**
 * Reconnects to an in-progress message stream when loading a conversation
 * where the last message has async_status 'IN_PROGRESS'
 * Currently only connects to Deep Research messages
 */
const reconnectToInProgressStream = createSafeAction(
	StoreActionLoggableName.reconnectToInProgressStream,
	({
		conversationId,
		messageId,
		lastEventId,
		isDeepResearch,
		abortCurrentRequest,
		answerIndex,
		onComplete,
		isAutoReconnect,
	}: ReconnectToInProgressStreamParams): ChatStoreAction =>
		reconnectToInProgressStreamProvider({
			conversationId,
			messageId,
			lastEventId,
			isDeepResearch,
			abortCurrentRequest,
			answerIndex,
			onComplete,
			isAutoReconnect,
			setConversationData,
			createTaskFromStream,
			setTaskProgressState,
			setTaskTodoSnapshot,
		}),
	{
		experienceName: () => ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
	},
);

/**
 * Processes raw chat messages from the API into StoreChatItem[] with boundary content extraction.
 * Shared by fetchConversation and warmConversationCache.
 */
export const processConversationMessages = ({
	latestMessages,
	conversationId,
	sendAnalyticsEvent,
}: {
	latestMessages: Awaited<ReturnType<ChatStoreContainerProps['assistanceService']['getChat']>>;
	conversationId: string;
	sendAnalyticsEvent: ChatStoreContainerProps['sendAnalyticsEvent'];
}): StoreChatItem[] => {
	const messages: StoreChatItem[] = [];

	for (const message of latestMessages) {
		if (isHumanMessage(message)) {
			messages.push(createStoreHumanWithContent(message));
		} else if (isStreamResponse(message)) {
			// Process boundary content for historical assistant messages
			let processedMessage = message;
			const markdownData: Record<string, string> = {};

			// Apply the same boundary content extraction logic as streaming messages
			if (fg('rovo_enable_data_sculptor')) {
				const sculptorMsg = extractMarkdownContent({
					streamMessage: processedMessage,
					boundaryStart: '[^sculptor:start^]',
					boundaryEnd: '[^sculptor:end^]',
					replacementContent: '[^sculptor^]',
				});
				processedMessage = sculptorMsg.processedStreamMessage as StreamResponse;
				sculptorMsg.boundaryContents.forEach((content, index) => {
					markdownData[`sculptor-${index}`] = content;
				});
			}

			const expandOpenTag = '▞';
			const expandCloseTag = '▚';
			const expandPlaceholder = '[^expand^]';

			const expandMsg = extractMarkdownContent({
				streamMessage: processedMessage,
				boundaryStart: expandOpenTag,
				boundaryEnd: expandCloseTag,
				replacementContent: expandPlaceholder,
			});
			processedMessage = expandMsg.processedStreamMessage as StreamResponse;
			expandMsg.boundaryContents.forEach((content, index) => {
				markdownData[`expand-${index}`] = content;
			});

			const chartPlaceholder = '[^chart^]';
			// When processing conversation history, process both unicode and non-unicode tags
			// to handle previous conversations that used the non-unicode tag
			// Once di_chat_chart_tag_unicode is rolled out, chartMsg can be removed after 30 days
			let chartIndex = 0;

			const chartMsg = extractMarkdownContent({
				streamMessage: processedMessage,
				boundaryStart: '[^chart:start^]',
				boundaryEnd: '[^chart:end^]',
				replacementContent: chartPlaceholder,
			});
			processedMessage = chartMsg.processedStreamMessage as StreamResponse;
			chartMsg.boundaryContents.forEach((content) => {
				markdownData[`chart-${chartIndex}`] = content;
				chartIndex++;
			});

			if (chartMsg?.boundaryContents && chartMsg.boundaryContents.length > 0) {
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'extracted',
					actionSubject: 'vizPluginChartNonUnicode',
					attributes: {
						messageId: message?.message?.message?.id.toString(),
						conversationId,
						chartCount: chartMsg.boundaryContents.length,
						timestamp: new Date().toISOString(),
					},
				});
			}

			const unicodeChartMsg = extractMarkdownContent({
				streamMessage: processedMessage,
				boundaryStart: '◴',
				boundaryEnd: '◶',
				replacementContent: chartPlaceholder,
			});
			processedMessage = unicodeChartMsg.processedStreamMessage as StreamResponse;
			unicodeChartMsg.boundaryContents.forEach((content) => {
				markdownData[`chart-${chartIndex}`] = content;
				chartIndex++;
			});

			if (fg('rovo_chat_enable_inline_lumina')) {
				const luminaMsg = extractMarkdownContent({
					streamMessage: processedMessage,
					boundaryStart: '◐',
					boundaryEnd: '◑',
					replacementContent: '\n\n[^lumina^]\n\n',
				});
				processedMessage = luminaMsg.processedStreamMessage as StreamResponse;
				luminaMsg.boundaryContents.forEach((content, index) => {
					markdownData[`lumina-${index}`] = content;
				});
			}

			// Create StoreAssistant with processed boundary data
			const storeAssistant = createStoreAssistantWithContent(processedMessage);
			if (Object.keys(markdownData).length > 0) {
				storeAssistant.markdownData = markdownData;
			}

			if (fg('studio_fe_slos')) {
				// Mark message as historical since it's loaded from conversation history
				storeAssistant.isHistorical = true;
			}

			messages.push(storeAssistant);
		}
	}

	return messages;
};

/**
 * Extracts agentVersion from the first assistant message's agent_author field.
 * Only loops until the first assistant message is found.
 */
export const extractAgentVersionFromMessages = (
	latestMessages: Awaited<ReturnType<ChatStoreContainerProps['assistanceService']['getChat']>>,
): AgentVersion | undefined => {
	if (!fg('rovo_agent_versioning_enabled')) {
		return undefined;
	}

	for (const message of latestMessages) {
		if (isStreamResponse(message)) {
			const agentAuthor = message.message?.message?.agent_author;
			if (agentAuthor?.versionId || agentAuthor?.versionType) {
				return {
					versionId: agentAuthor.versionId,
					versionType: agentAuthor.versionType,
					versionNumber: agentAuthor.versionNumber,
				};
			}
			return undefined;
		}
	}

	return undefined;
};

const fetchConversation = createSafeAction(
	StoreActionLoggableName.fetchConversation,
	({
		conversationId: conversationIdParam,
		messageIdSelectedForPreview,
	}: {
		conversationId?: string;
		messageIdSelectedForPreview?: string | null;
	}): ChatStoreAction =>
		async (
			{ getState, dispatch },
			{
				assistanceService,
				experienceTracker,
				sendAnalyticsEvent,
				getMessages,
				setMessages,
				showFlag,
			},
		) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;

			const conversation = getState().conversations[conversationId];

			if (conversation?.status !== CHAT_STORE_STATUS.idle || isEmptyConversation(conversationId)) {
				return;
			}

			if (conversation.skipNextFetch) {
				dispatch(
					setConversationData({
						conversationId,
						data: { skipNextFetch: false },
					}),
				);
				return;
			}

			experienceTracker.start({
				name: ExperienceName.FETCH_CHAT,
				id: conversationId,
				attributes: {
					conversationId,
				},
			});

			if (conversation.storeAbortController) {
				await conversation.storeAbortController?.abort();
			}

			dispatch(
				initializeConversationData({
					conversationId,
					data: {
						id: conversationId,
						name: conversation.name,
						timeCreated: conversation.timeCreated,
						status: CHAT_STORE_STATUS.loadingChat,
						traces:
							messageIdSelectedForPreview && fg('aifc_create_enabled')
								? []
								: [{ messageContent: 'Reloading chat', status: 'loading' }],
						sourceAgentId: getSourceAgentId(
							getMessages(conversationId),
							getConversation(getState(), conversationId),
						),
						messages: [],
					},
				}),
			);

			let serviceChat: Awaited<ReturnType<typeof assistanceService.getChat>>;
			try {
				serviceChat = await assistanceService.getChat(conversationId);
			} catch (e) {
				if (isChannelNotFoundError(e)) {
					//We succeed this event because in sendMessage, we create a new channel if we receive a 404 response
					// Users will not see this error, so exclude it from Sentry
					//This is a fix due to an incompatible BE API change
					sendAnalyticsEvent({
						eventType: 'operational',
						action: 'notFound',
						actionSubject: 'rovoChatFetchConversation',
					});

					experienceTracker.succeed({
						name: ExperienceName.FETCH_CHAT,
					});

					dispatch(
						createConversationOptimistically({
							sourceAgentId: getSourceAgentId(
								getMessages(conversationId),
								getConversation(getState(), conversationId),
							),
						}),
					);
					dispatch(removeConversationFromStore({ conversationId }));

					if (fg('rovo_supress_deleted_convo_errors')) {
						return;
					}
				}

				if (
					e instanceof ApiError &&
					e.errorAttributes?.statusCode === 403 &&
					fg('rovo_chat_improved_conversation_403_handling')
				) {
					const rovoParams = getRovoParams();
					const isConversationIdInRovoParams = !!rovoParams?.conversationId;

					sendAnalyticsEvent({
						eventType: 'track',
						action: 'forbidden',
						actionSubject: 'rovoChatFetchConversation',
						attributes: {
							isConversationIdInRovoParams,
						},
					});

					experienceTracker.fail({
						name: ExperienceName.FETCH_CHAT,
						error: e,
					});

					dispatch(removeConversationFromStore({ conversationId }));
					dispatch(setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID }));

					if (isConversationIdInRovoParams) {
						updatePageRovoParams({
							conversationId: undefined,
						});
					}

					showFlag?.(StoreErrorFlagId.CONVERSATION_FORBIDDEN);

					return;
				}

				throw e;
			}

			// There's a bug currently in the playwright pubsub test where when adding in a new
			// async function to sendMessageStream it causes the message to be dropped
			const status = getState().conversations[conversationId]?.status;

			if (status !== CHAT_STORE_STATUS.loadingChat) {
				return;
			}

			const latestMessages = serviceChat;

			const messages = processConversationMessages({
				latestMessages,
				conversationId,
				sendAnalyticsEvent,
			});

			const fetchedAgentVersion = extractAgentVersionFromMessages(latestMessages);

			dispatch(
				setConversationData({
					conversationId,
					data: {
						...clearStatus({ lastAgentMessageStatus: 'SUCCESSFUL' }),
						sourceAgentId: getSourceAgentId(
							getMessages(conversationId),
							getConversation(getState(), conversationId),
						),
						...(fetchedAgentVersion ? { agentVersion: fetchedAgentVersion } : {}),
					},
				}),
			);

			// Check if the last message has async_status 'IN_PROGRESS' and reconnect to the stream
			const lastMessage = latestMessages.at(-1);
			const isDeepResearch = (lastMessage as StreamResponse)?.message?.message?.message_metadata
				?.deep_research;

			if (
				isInProgressStreamResponse(lastMessage) &&
				(isDeepResearch ||
					FeatureGates.getExperimentValue('rovo_chat_reconnect_all_experiment', 'isEnabled', false))
			) {
				const messageId = lastMessage.message.message.id;
				// Clear the "Message is in progress" content of the last agent message
				const updatedMessages = clearStoreAssistantMessageContent(messages, messageId);
				setMessages(conversationId, updatedMessages);

				dispatch(
					reconnectToInProgressStream({
						conversationId,
						messageId,
						isDeepResearch,
						isAutoReconnect: true,
					}),
				);
			} else {
				setMessages(conversationId, messages);
			}

			experienceTracker.succeed({
				name: ExperienceName.FETCH_CHAT,
			});
		},
	{
		experienceName: () => ExperienceName.FETCH_CHAT,
	},
);

// Abort requests for a specific conversation, or ALL conversations if no conversationId is passed
const abortRequests = createSafeAction(
	StoreActionLoggableName.abortRequests,
	({
		conversationId,
		thinkDeeperEnabled,
		fastModeEnabled,
		taskModeEnabled,
		deepResearchEnabled,
	}: {
		conversationId?: string;
		deepResearchEnabled?: boolean;
		thinkDeeperEnabled?: boolean;
		fastModeEnabled?: boolean;
		taskModeEnabled?: boolean;
	} = {}): ChatStoreAction =>
		({ getState, dispatch }, { experienceTracker, setReasoningMode }) => {
			const conversations = getState().conversations;

			const abortConversationStream = (conversation: Conversation) => {
				if (conversation?.storeAbortController) {
					experienceTracker.start({
						name: ExperienceName.ABORT_AGENT_STREAM,
						id: conversation?.id,
						attributes: {
							conversationId: conversation?.id,
						},
					});

					conversation.storeAbortController.abort();

					experienceTracker.abort({
						name: sendMessageExperienceName({
							deepResearchEnabled,
							thinkDeeperEnabled,
							fastModeEnabled,
						}),
						reason: 'Aborted',
						attributes: {
							isProgrammaticAbort: true,
						},
					});

					experienceTracker.succeed({
						name: ExperienceName.ABORT_AGENT_STREAM,
					});

					if (taskModeEnabled && conversation?.id) {
						dispatch(
							setTaskProgressState({
								conversationId: conversation.id,
								progressState: 'Cancelled',
							}),
						);
					}
				}
			};

			if (conversationId) {
				abortConversationStream(conversations[conversationId]);
			} else {
				Object.values(conversations).forEach(abortConversationStream);
			}
		},
	{
		experienceName: () => ExperienceName.ABORT_AGENT_STREAM,
	},
);

/**
 * Triggers Answer Now functionality to fast-forward extended thinking.
 * This action performs an optimistic update:
 * 1. Sets answerNowStatus to SWITCHING immediately
 * 2. Calls the Answer Now API endpoint
 * 3. Sets status to ERRORED if API call fails (hides button, prevents retry)
 *
 * Note: Research steps and message content are NOT cleared here - they continue
 * to be displayed during SWITCHING. Clearing happens when FAST_MODE_ENABLED is received.
 */
const answerNow = createSafeAction(
	StoreActionLoggableName.answerNow,
	({
		conversationId: conversationIdParam,
		tmpMessageId,
		remoteMessageId,
	}: {
		conversationId?: string;
		tmpMessageId: string; // For store operations (finding/updating the message)
		remoteMessageId: string; // For the API call
	}): ChatStoreAction =>
		async (
			{ getState, setState },
			{ assistanceService, experienceTracker, getMessages, setMessages },
		) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;

			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return;
			}

			// Helper to update conversation traces
			const updateConversationTrace = (traceMessage: string) => {
				const state = getState();
				const conversation = state.conversations[conversationId];
				if (conversation) {
					setState({
						conversations: {
							...state.conversations,
							[conversationId]: {
								...conversation,
								traces: [{ messageContent: traceMessage }],
							},
						},
					});
				}
			};

			// Verify the message exists and has answerNowStatus === AVAILABLE
			// Also prevents duplicate requests if status is already SWITCHING
			const messages = getMessages(conversationId);
			const targetMessage = messages.find(
				(msg) => isStoreAssistant(msg) && msg.content?.message?.message?.id === tmpMessageId,
			);

			if (
				!targetMessage ||
				!isStoreAssistant(targetMessage) ||
				!isAnswerNowAvailable(targetMessage.answerNowStatus)
			) {
				return;
			}

			// Optimistic update: Set SWITCHING status first and update trace message
			const updatedMessages = updateStoreAssistantMessageAnswerNowStatus(
				getMessages(conversationId),
				tmpMessageId,
				{ type: AnswerNowStatusType.SWITCHING },
			);
			setMessages(conversationId, updatedMessages);
			updateConversationTrace('Generating answer now!');

			// Start experience tracking
			experienceTracker.start({
				name: ExperienceName.ANSWER_NOW,
				id: conversationId,
				attributes: {
					conversationId,
					remoteMessageId,
					tmpMessageId,
				},
			});

			try {
				// Call the Answer Now API with the remote (BE-provided) message ID
				await assistanceService.answerNow(conversationId, remoteMessageId);

				// Only set SWITCHING_REQUESTED if status is still SWITCHING (race condition guard)
				const currentMessages = getMessages(conversationId);
				const currentMessage = currentMessages.find(
					(msg) => isStoreAssistant(msg) && msg.content?.message?.message?.id === tmpMessageId,
				);
				if (
					currentMessage &&
					isStoreAssistant(currentMessage) &&
					currentMessage.answerNowStatus?.type === AnswerNowStatusType.SWITCHING
				) {
					const successMessages = updateStoreAssistantMessageAnswerNowStatus(
						currentMessages,
						tmpMessageId,
						{ type: AnswerNowStatusType.SWITCHING_REQUESTED },
					);
					setMessages(conversationId, successMessages);
				}

				// Track success
				experienceTracker.succeed({
					name: ExperienceName.ANSWER_NOW,
				});
			} catch (error) {
				// Only set ERRORED if status is still SWITCHING (race condition guard)
				const currentMessages = getMessages(conversationId);
				const currentMessage = currentMessages.find(
					(msg) => isStoreAssistant(msg) && msg.content?.message?.message?.id === tmpMessageId,
				);
				if (
					currentMessage &&
					isStoreAssistant(currentMessage) &&
					currentMessage.answerNowStatus?.type === AnswerNowStatusType.SWITCHING
				) {
					const erroredMessages = updateStoreAssistantMessageAnswerNowStatus(
						currentMessages,
						tmpMessageId,
						{ type: AnswerNowStatusType.ERRORED },
					);
					setMessages(conversationId, erroredMessages);
					updateConversationTrace('Generating faster answer failed, continuing with current mode');
				}

				// Track failure
				experienceTracker.fail({
					name: ExperienceName.ANSWER_NOW,
					error: error instanceof Error ? error : new Error(String(error)),
				});

				throw error;
			}
		},
);

const addErrorMessage = createSafeAction(
	StoreActionLoggableName.addErrorMessage,
	({
		error,
		conversationId,
		errorPrefix,
	}: {
		error: unknown;
		conversationId?: string;
		errorPrefix?: string;
	}): ChatStoreAction =>
		({ dispatch }) => {
			dispatch(
				addStoreChatItem({
					message: createStoreError(error, errorPrefix),
					conversationId,
				}),
			);
		},
);

export type InitConversationChannelsDataArgs = {
	autoSelectConversation?: boolean;
	isInitialLoad?: boolean;
	onError?: OnErrorCallback<InitConversationChannelsDataArgs>;
};

const initConversationChannelsData = createSafeAction(
	StoreActionLoggableName.initConversationChannelsData,
	({
		autoSelectConversation = true,
		isInitialLoad = false,
	}: InitConversationChannelsDataArgs = {}): ChatStoreAction =>
		async (
			{ setState, getState, dispatch },
			{
				siteId,
				assistanceService,
				experienceTracker,
				setChatStatus,
				initChatInitiator,
				insertHighlightActionsEditorPlaceholder,
				setStagingAreaMode,
				setMessageIdSelectedForPreview,
			},
		) => {
			if (isInitialLoad) {
				experienceTracker.start({
					id: ExperienceName.GET_CONVERSATION_CHANNELS_INITIAL_LOAD,
					name: ExperienceName.GET_CONVERSATION_CHANNELS_INITIAL_LOAD,
				});
			} else {
				experienceTracker.start({
					id: ExperienceName.GET_CONVERSATION_CHANNELS,
					name: ExperienceName.GET_CONVERSATION_CHANNELS,
				});
			}

			const rovoParams = getRovoParams();
			let initWithParams = false;

			// Capture conversation IDs before API call to detect changes during the call
			const conversationsBeforeAPICall = new Set(Object.keys(getState().conversations));

			let channelsFromAPI = await assistanceService.getConversationChannels();
			const { conversations, currentConversationId } = getState();

			const channelIds = new Set(channelsFromAPI.map((channel) => channel.id));

			// Identify conversations deleted during the API call (were there before but aren't now)
			const conversationsDeletedDuringCall = new Set(
				Array.from(conversationsBeforeAPICall).filter(
					(id) => !conversations[id] && !isEmptyConversation(id),
				),
			);

			// Identify conversations created during the API call (exist in state but weren't there before and aren't in API response)
			const conversationsCreatedDuringCall = new Set(
				Object.keys(conversations).filter(
					(id) => !conversationsBeforeAPICall.has(id) && !channelIds.has(id),
				),
			);

			const processedChannels = channelsFromAPI
				.filter((channel) => !conversationsDeletedDuringCall.has(channel.id)) // Exclude deleted conversations
				.reduce(
					(acc, channel) => {
						acc[channel.id] = FeatureGates.getExperimentValue(
							'rovo_chat_perf_init_load_optimization',
							'isBackgroundConversationsFetchEnabled',
							false,
						)
							? mergeConversationWithNetworkPriority(channel, conversations[channel.id])
							: (conversations[channel.id] ?? {
									name: channel.name ?? generateConversationName(),
									id: channel.id,
									messages: [],
									status: CHAT_STORE_STATUS.idle,
									traces: [],
									timeCreated: channel.time_created,
									lastMessage: channel.last_message,
									lastMessageType: channel.last_message_type ?? [],
									lastMessageTimestamp: channel.last_message_timestamp,
									agent: channel.agent,
									lastAgentMessageStatus: channel.last_agent_message_status,
									readStatus: channel.readStatus,
									scopes: channel.scopes,
									...(channel.tags !== undefined ? { tags: channel.tags } : {}),
								});

						return acc;
					},
					{} as Record<string, Conversation>,
				);

			// Preserve conversations created during the API call to avoid race condition
			const conversationsToPreserve = Array.from(conversationsCreatedDuringCall).reduce(
				(acc, id) => {
					acc[id] = conversations[id];
					return acc;
				},
				{} as Record<string, Conversation>,
			);

			// Hydrate the store with the fetched conversation channels
			// whilst preserving the empty state and and any conversations created during the API call, so drafts are never lost
			// due to rehydration (e.g window refocus) when this is called
			setState({
				conversations: {
					EMPTY_CONVERSATION_ID: conversations[EMPTY_CONVERSATION_ID],
					...processedChannels,
					...conversationsToPreserve,
				},
			});

			if (rovoParams.stagingAreaOpen && fg('aifc_create_enabled')) {
				setStagingAreaMode('inline', 'rovoChat');
				updatePageRovoParams({ stagingAreaOpen: undefined });
			}
			if (rovoParams.messageIdSelectedForPreview && fg('aifc_create_enabled')) {
				setMessageIdSelectedForPreview(rovoParams.messageIdSelectedForPreview);
				updatePageRovoParams({ messageIdSelectedForPreview: undefined });
			}

			if (rovoParams.triggerOpen) {
				// if the rovo chat was actually open with url param
				initChatInitiator(rovoParams.initiator);
			}

			// Process params as a priority
			if (autoSelectConversation && rovoParams.pathway === 'chat') {
				// Skip standard init if we're processing params
				// TODO: Logic will simplify dramatically when FF are cleaned up this just ensures a safe killswitch
				initWithParams = true;

				if (rovoParams.conversationId) {
					dispatch(
						setSelectedConversation({
							conversationId: rovoParams.conversationId,
							sourceAgentId: rovoParams.agentId,
						}),
					);
				} else {
					dispatch(
						createConversationOptimistically({
							sourceAgentId: rovoParams.agentId,
						}),
					);
				}

				if (rovoParams.prompt) {
					// For cases where the prompt is a json string containing ADF, we need to parse it
					const shouldParse = isValidAdfJsonString(rovoParams.prompt);
					const prompt = shouldParse
						? (JSON.parse(rovoParams.prompt) as DocNode)
						: rovoParams.prompt;

					if (rovoParams.insertPrompt === 'highlight-action') {
						insertHighlightActionsEditorPlaceholder(prompt as DocNode);
					} else {
						dispatch(
							sendMessage({
								prompt: encodeAdf(prompt),
								agentId: rovoParams.agentId,
								// Default to false as a URL could be clicked from anywhere
								// and we don't want store messages without the user knowing
								// Getting preferences in the store would also required the hook to be passed
								// on the  store container which is unncessary
								storeUserMessages: false,
								messageSources: {
									interactionSource: 'urlParam',
								},
							}),
						);
					}
				}
			}

			if (!initWithParams) {
				// If conversation isn't empty state and it doesn't exist on the server, then assume deleted
				// BUT: exclude conversations that were created during the API call
				const conversationDeleted =
					!isEmptyConversation(currentConversationId) &&
					!channelIds.has(currentConversationId) &&
					!conversationsCreatedDuringCall.has(currentConversationId);

				// Fallback conversation used when we cannot reliably select one
				// due to being deleted, or because there's no target chat in localStorage
				const fallbackConversation = Object.keys(processedChannels).find(
					(id) => !isEmptyConversation(id),
				);

				const targetChatId =
					localStorage?.getItem(getChatLoadPreferenceKey(siteId)) ?? EMPTY_CONVERSATION_ID;

				const targetChatExists =
					isEmptyConversation(targetChatId) ||
					channelIds.has(targetChatId) ||
					conversationsCreatedDuringCall.has(targetChatId);

				// Detect if conversation is idle, to prevent auto-selection occurring
				// when still awaiting a response to help prevent optimistic conversations
				// closing because the API doens't ye thave the new conversation ID for it
				// to show up in channelsFromAPI
				const currentConversationIdle =
					conversations[currentConversationId]?.status === CHAT_STORE_STATUS.idle;

				// WHEN autoSselectConversation is true (default behaviour, except for window refocus event)
				// AND current conversation is not the target conversation
				// OR the currently selected conversation is deleted (e.g. an old window is refocussed)
				const shouldSelectChat =
					(autoSelectConversation && currentConversationId !== targetChatId) ||
					(currentConversationIdle && conversationDeleted);

				const lastMessageSentAt = getLastMessageSentAt();

				const timeoutBeforeNewChat = SHORTER_TIMEOUT_BEFORE_NEW_CHAT;

				if (
					autoSelectConversation &&
					lastMessageSentAt &&
					Date.now() - lastMessageSentAt >= timeoutBeforeNewChat
				) {
					dispatch(
						setSelectedConversation({
							conversationId: EMPTY_CONVERSATION_ID,
						}),
					);

					setLastMessageSentAt(null);
				} else if (shouldSelectChat) {
					if (targetChatExists) {
						// Prefer last seen chat
						dispatch(
							setSelectedConversation({
								conversationId: targetChatId,
							}),
						);
					} else if (fallbackConversation) {
						// Otherwise, fallback to the first non-empty conversation
						dispatch(
							setSelectedConversation({
								conversationId: fallbackConversation,
							}),
						);
					} else {
						dispatch(
							setSelectedConversation({
								conversationId: EMPTY_CONVERSATION_ID,
							}),
						);
					}
				}
			}

			// Set online status later, to ensure that pubsub listener doesn't race condition with store init state
			setChatStatus('online');

			experienceTracker.succeed({
				name: isInitialLoad
					? ExperienceName.GET_CONVERSATION_CHANNELS_INITIAL_LOAD
					: ExperienceName.GET_CONVERSATION_CHANNELS,
			});
		},
	{
		experienceName: (args) =>
			args?.[0]?.isInitialLoad
				? ExperienceName.GET_CONVERSATION_CHANNELS_INITIAL_LOAD
				: ExperienceName.GET_CONVERSATION_CHANNELS,
		actionErrorHandler: (context) => {
			const { onError, ...args } = context.args?.[0] ?? {};
			return onError ? onError({ ...context, args }) : { proceedToDefaultHandling: true };
		},
	},
);

export type FetchConversationChannelsDataArgs = {
	isInitialLoad?: boolean;
	onError?: OnErrorCallback<FetchConversationChannelsDataArgs>;
};

const fetchConversationChannelsData = createSafeAction(
	StoreActionLoggableName.initConversationChannelsData,
	({ isInitialLoad = false }: FetchConversationChannelsDataArgs = {}): ChatStoreAction =>
		async ({ setState, getState, dispatch }, { assistanceService, experienceTracker }) => {
			if (isInitialLoad) {
				experienceTracker.start({
					id: ExperienceName.GET_CONVERSATION_CHANNELS_INITIAL_LOAD,
					name: ExperienceName.GET_CONVERSATION_CHANNELS_INITIAL_LOAD,
				});
			} else {
				experienceTracker.start({
					id: ExperienceName.GET_CONVERSATION_CHANNELS,
					name: ExperienceName.GET_CONVERSATION_CHANNELS,
				});
			}

			// Capture conversation IDs before API call to detect changes during the call
			const conversationsBeforeAPICall = new Set(Object.keys(getState().conversations));

			let channelsFromAPI = await assistanceService.getConversationChannels();
			const { conversations, currentConversationId } = getState();

			const channelIds = new Set(channelsFromAPI.map((channel) => channel.id));

			// Identify conversations deleted during the API call (were there before but aren't now)
			const conversationsDeletedDuringCall = new Set(
				Array.from(conversationsBeforeAPICall).filter(
					(id) => !conversations[id] && !isEmptyConversation(id),
				),
			);

			// Identify conversations created during the API call (exist in state but weren't there before and aren't in API response)
			const conversationsCreatedDuringCall = new Set(
				Object.keys(conversations).filter(
					(id) => !conversationsBeforeAPICall.has(id) && !channelIds.has(id),
				),
			);

			// Preserve all local conversations not yet on the server
			// (they may have just finished processing or be in-flight)
			// e.g. optimistic conversation when URL params are used to open a chat
			const optimisticConversationsToPreserve = new Set(
				Object.keys(conversations).filter((id) => !channelIds.has(id) && !isEmptyConversation(id)),
			);

			const conversationIdsToPreserve = new Set([
				...conversationsCreatedDuringCall,
				...optimisticConversationsToPreserve,
			]);

			const conversationsToPreserve = Object.fromEntries(
				Array.from(conversationIdsToPreserve, (id) => [id, conversations[id]]),
			);

			// Build a reverse lookup from backendConversationId → local optimistic conversation.
			// This allows us to find the local entry (which carries rovoSpaceId and other
			// optimistic fields) even when the API returns the backend-assigned ID which differs
			// from the client-generated UUID stored in the local conversation.
			const localConversationByBackendId = new Map<string, Conversation>(
				Object.values(conversations)
					.filter((c): c is Conversation & { backendConversationId: string } =>
						Boolean(c.backendConversationId),
					)
					.map((c) => [c.backendConversationId, c]),
			);

			const processedChannels = channelsFromAPI
				.filter((channel) => !conversationsDeletedDuringCall.has(channel.id)) // Exclude deleted conversations
				.reduce(
					(acc, channel) => {
						// Look up the local conversation first by its direct ID, then by the
						// backendConversationId that was recorded on the optimistic entry once the
						// FINAL_RESPONSE arrived. This ensures fields like rovoSpaceId that are
						// set on the optimistic conversation are preserved in the merged result.
						const localConversation =
							conversations[channel.id] ?? localConversationByBackendId.get(channel.id);
						acc[channel.id] = mergeConversationWithNetworkPriority(channel, localConversation);

						return acc;
					},
					{} as Record<string, Conversation>,
				);

			// Hydrate the store with the fetched conversation channels
			// whilst preserving the empty state and and any conversations created during the API call, so drafts are never lost
			// due to rehydration (e.g window refocus) when this is called
			setState({
				conversations: {
					EMPTY_CONVERSATION_ID: conversations[EMPTY_CONVERSATION_ID],
					...processedChannels,
					...conversationsToPreserve,
				},
			});

			const currentConversationDeleted =
				!isEmptyConversation(currentConversationId) &&
				!channelIds.has(currentConversationId) &&
				!conversationIdsToPreserve.has(currentConversationId);

			if (currentConversationDeleted && !isInitialLoad) {
				dispatch(setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID }));
			}

			if (isInitialLoad && fg('rovo_chat_enable_tasks')) {
				dispatch(restoreTaskModeIfNeeded());
			}

			experienceTracker.succeed({
				name: isInitialLoad
					? ExperienceName.GET_CONVERSATION_CHANNELS_INITIAL_LOAD
					: ExperienceName.GET_CONVERSATION_CHANNELS,
			});
		},
	{
		experienceName: (args) =>
			args?.[0]?.isInitialLoad
				? ExperienceName.GET_CONVERSATION_CHANNELS_INITIAL_LOAD
				: ExperienceName.GET_CONVERSATION_CHANNELS,
		actionErrorHandler: (context) => {
			const { onError, ...args } = context.args?.[0] ?? {};
			return onError ? onError({ ...context, args }) : { proceedToDefaultHandling: true };
		},
	},
);

const getTimeoutBeforeExpiration = (): number => {
	return SHORTER_TIMEOUT_BEFORE_NEW_CHAT;
};

/*
 * This action runs on initial load to hydrate this in-memory store (chatStore)
 * with data from either URL parameters or LocalStorage. URL parameters take precedence.
 *
 * Setting the currentConversationId (using setSelectedConversation or createConversationOptimistically)
 * may implicitly trigger fetching of conversation messages.
 *
 * Any change to currentConversationId will trigger a useEffect in ChatHistory,
 * which then executes the `fetchConversation` action.
 */
const hydrateConversationWithExternalParams = createSafeAction(
	StoreActionLoggableName.hydrateConversation,
	({ autoSelectConversation }: { autoSelectConversation: boolean }): ChatStoreAction =>
		async (
			{ getState, dispatch },
			{
				siteId,
				experienceTracker,
				setChatStatus,
				initChatInitiator,
				insertHighlightActionsEditorPlaceholder,
				setStagingAreaMode,
				setMessageIdSelectedForPreview,
				getStoreUserMessagesPreference,
			},
		) => {
			experienceTracker.start({
				id: ExperienceName.HYDRATE_CONVERSATION,
				name: ExperienceName.HYDRATE_CONVERSATION,
			});

			const rovoParamsUnsanitized = getRovoParams();
			const rovoParams = Object.fromEntries(
				Object.entries(rovoParamsUnsanitized).filter(([, v]) => v !== 'undefined'),
			) as typeof rovoParamsUnsanitized;

			let initWithParams = autoSelectConversation && rovoParams.pathway === 'chat';
			const { currentConversationId } = getState();

			if (rovoParams.stagingAreaOpen && fg('aifc_create_enabled')) {
				setStagingAreaMode('inline', 'rovoChat');
				updatePageRovoParams({ stagingAreaOpen: undefined });
			}
			if (rovoParams.messageIdSelectedForPreview && fg('aifc_create_enabled')) {
				setMessageIdSelectedForPreview(rovoParams.messageIdSelectedForPreview);
				updatePageRovoParams({ messageIdSelectedForPreview: undefined });
			}

			if (rovoParams.triggerOpen) {
				// if the rovo chat was actually open with url param
				initChatInitiator(rovoParams.initiator);
			}

			// Process params as a priority
			if (initWithParams) {
				const localConversation = rovoParams.conversationId
					? getState().conversations[rovoParams.conversationId]
					: undefined;

				if (rovoParams.conversationId && localConversation) {
					dispatch(
						setSelectedConversation({
							conversationId: rovoParams.conversationId,
							sourceAgentId: rovoParams.agentId,
						}),
					);
				} else {
					dispatch(
						createConversationOptimistically({
							id: rovoParams.conversationId,
							sourceAgentId: rovoParams.agentId,
						}),
					);
				}

				if (rovoParams.prompt) {
					// For cases where the prompt is a json string containing ADF, we need to parse it
					const shouldParse = isValidAdfJsonString(rovoParams.prompt);
					const prompt = shouldParse
						? (JSON.parse(rovoParams.prompt) as DocNode)
						: rovoParams.prompt;

					if (rovoParams.insertPrompt === 'highlight-action') {
						insertHighlightActionsEditorPlaceholder(prompt as DocNode);
					} else {
						dispatch(
							sendMessage({
								prompt: encodeAdf(prompt),
								agentId: rovoParams.agentId,
								// Use the user's preference for storing messages
								storeUserMessages: getStoreUserMessagesPreference?.() ?? false,
								messageSources: {
									interactionSource: 'urlParam',
								},
							}),
						);
					}
				}

				// for UFO performance tracking dimensions
				addUFOCustomData({
					isEmptyConversation: !rovoParams.conversationId,
					hasInitialPrompt: !!rovoParams.prompt,
				});
			} else if (autoSelectConversation) {
				const timeoutBeforeNewChat = getTimeoutBeforeExpiration();
				const targetConversationId =
					localStorage?.getItem(getChatLoadPreferenceKey(siteId)) ?? EMPTY_CONVERSATION_ID;
				const lastMessageSentAt = getLastMessageSentAt();
				const hasTargetChanged = targetConversationId !== currentConversationId;
				const isTargetExpired =
					lastMessageSentAt && Date.now() - lastMessageSentAt >= timeoutBeforeNewChat;
				const isTargetConversationEmpty = isEmptyConversation(targetConversationId);
				const hasLocalTargetConversation = Boolean(getState().conversations[targetConversationId]);

				// start new conversation: if recent expired
				if (isTargetExpired) {
					dispatch(setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID }));
					setLastMessageSentAt(null);
					// start new conversation: if target conversation was set to new (e.g. in another tab)
				} else if (hasTargetChanged && isTargetConversationEmpty) {
					dispatch(setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID }));
					// select existing conversation: if convo exists in memory
				} else if (hasTargetChanged && hasLocalTargetConversation) {
					dispatch(setSelectedConversation({ conversationId: targetConversationId }));
					// create a new optimistic conversation: renders ChatHistory which triggers API call to fetch messages
				} else if (hasTargetChanged) {
					dispatch(createConversationOptimistically({ id: targetConversationId }));
				}

				// for UFO performance tracking dimensions
				addUFOCustomData({ isEmptyConversation: !isTargetExpired && hasTargetChanged });
			}

			// Set online status later, to ensure that pubsub listener doesn't race condition with store init state
			setChatStatus('online');

			experienceTracker.succeed({ name: ExperienceName.HYDRATE_CONVERSATION });
		},
	{
		experienceName: () => ExperienceName.HYDRATE_CONVERSATION,
	},
);

const addAgentMessage = createSafeAction(
	StoreActionLoggableName.addAgentMessage,
	({
		conversationId,
		agentNamedId,
		content,
	}: {
		agentNamedId: string;
		conversationId: string;
		content: string;
	}): ChatStoreAction =>
		({ dispatch }) => {
			dispatch(
				addAssistantMessage({
					id: generateId(),
					content,
					conversationId,
					agentNamedId,
				}),
			);
		},
);

const handleChatOpenPubsubAction = createSafeAction(
	StoreActionLoggableName.handleChatOpenPubsubAction,
	({ conversationId, agentId }: { conversationId: string; agentId?: string }): ChatStoreAction =>
		async ({ getState, dispatch }) => {
			// IF convo id is the current chat
			// refetch the chat, do not create new one
			if (conversationId === getState().currentConversationId) {
				// Store sourceAgentId so useConversation can derive agent details
				if (agentId && fg('jira_ai_adding_agent_id_improving_agent_hydration')) {
					dispatch(
						setConversationData({
							conversationId,
							data: { sourceAgentId: agentId },
						}),
					);
				}
				dispatch(fetchConversation({ conversationId }));
				return;
			}

			// IF chat already exist in conversation
			// switch to that convo -> and it will trigger refetch when ChatHistory is rendered
			if (getState().conversations[conversationId]) {
				// Store sourceAgentId so useConversation can derive agent details
				if (agentId && fg('jira_ai_adding_agent_id_improving_agent_hydration')) {
					dispatch(
						setConversationData({
							conversationId,
							data: { sourceAgentId: agentId },
						}),
					);
				}
				dispatch(
					setSelectedConversation({
						conversationId,
					}),
				);
				return;
			}

			// IF chat does not exist yet
			// Create conversation optimistically, which will switch to that conversation
			// By switching conversation, it will render ChatHistory
			// ChatHistory will auto fetch the chat data
			// if not found during `sendMessage`, it will auto create one and retry when encountered `404`
			// Create conversation optimistically and store sourceAgentId
			dispatch(
				createConversationOptimistically({
					id: conversationId,
					...(agentId && fg('jira_ai_adding_agent_id_improving_agent_hydration')
						? { sourceAgentId: agentId }
						: {}),
				}),
			);
		},
);

const createConversationOptimistically = createSafeAction(
	StoreActionLoggableName.createConversationOptimistically,
	(options: {
		name?: string;
		sourceId?: string;
		id?: string;
		timeCreated?: string;
		lastMessageTimestamp?: string;
		sourceAgentId?: string;
		status?: CHAT_STORE_STATUS;
		lastMessageType?: MessageType[];
		lastAgentMessageStatus?: LastAgentMessageStatus;
		agent?: AgentMetadata;
		agentVersion?: AgentVersion;
		rovoSpaceId?: string;
	}): ChatStoreAction =>
		async ({ dispatch }, { setMessages }) => {
			const conversationId = options?.id ?? EMPTY_CONVERSATION_ID;
			const time = new Date().toISOString();

			dispatch(
				initializeConversationData({
					conversationId,
					data: {
						id: conversationId,
						lastMessageTimestamp: options.lastMessageTimestamp ?? time,
						name: options.name ?? generateConversationName(),
						sourceAgentId: options.sourceAgentId,
						sourceId: options.sourceId ?? undefined,
						status: options?.status ?? CHAT_STORE_STATUS.idle,
						timeCreated: options?.timeCreated ?? time,
						traces: [],
						messages: [],
						lastMessageType: options?.lastMessageType ?? [],
						lastAgentMessageStatus: options?.lastAgentMessageStatus,
						...(options?.rovoSpaceId ? { rovoSpaceId: options.rovoSpaceId } : {}),
						...(options?.agent && fg('rovo_chat_chat_history_refresh')
							? { agent: options.agent }
							: {}),
						...(options?.agentVersion ? { agentVersion: options.agentVersion } : {}),
					},
				}),
			);

			setMessages(conversationId, []);

			dispatch(
				setSelectedConversation({
					conversationId,
				}),
			);
		},
);

const createSeededConversation = createSafeAction(
	StoreActionLoggableName.createSeededConversation,
	(options: {
		name?: string;
		dialogues: Dialogues[];
		sourceId?: string;
		id?: string;
		followUpSource: Omit<FollowUpSource, 'none'>;
		agentId?: string;
		agentVersion?: AgentVersion;
		tags?: string[];
		skipNextFetch?: boolean;
		// When the seeded conversation is scoped to a Rovo Space (e.g. created from
		// the SpaceLandingPage chat input or a space starter), set this so the
		// conversation appears under the space in the side nav and is excluded from
		// the regular conversation list immediately on optimistic creation.
		rovoSpaceId?: string;
		mode?: {
			deepResearchEnabled?: boolean;
			thinkDeeperEnabled?: boolean;
			fastModeEnabled?: boolean;
			taskModeEnabled?: boolean;
			webSearchEnabled?: boolean;
			useCurrentPageContext?: boolean;
			appFilters?: RovoApp[];
		};
	}): ChatStoreAction<Promise<string | undefined>> =>
		async (
			{ dispatch },
			{
				assistanceService,
				experienceTracker,
				sendAnalyticsEvent,
				setMessages,
				getChatInitiator,
				onNewConversationStartingWithSendMessage,
			},
		) => {
			experienceTracker.start({
				name: ExperienceName.CREATE_SEEDED_CONVERSATION_CHANNEL,
				id: ExperienceName.CREATE_SEEDED_CONVERSATION_CHANNEL,
			});

			const createdConversation = await assistanceService.createConversationChannel(options);
			const humanMessage = createdConversation.messages?.find((message) => isHumanMessage(message));
			const assistantMessage = createdConversation.messages?.find((message) =>
				isAssistantMessage(message),
			);
			const humanMessageId = humanMessage ? humanMessage.id : '';
			const assistantMessageId = assistantMessage ? assistantMessage.id : '';
			const conversationTags = createdConversation.tags ?? options.tags;

			dispatch(
				initializeConversationData({
					conversationId: createdConversation.id,
					data: {
						id: createdConversation.id,
						name: createdConversation.name,
						timeCreated: createdConversation.time_created,
						status: CHAT_STORE_STATUS.idle,
						traces: [],
						sourceId: options.sourceId ?? undefined,
						sourceAgentId: options.agentId,
						agentVersion: options.agentVersion,
						messages: [],
						...(createdConversation.agent && fg('rovo_chat_chat_history_refresh')
							? { agent: createdConversation.agent }
							: {}),
						...(conversationTags !== undefined ? { tags: conversationTags } : {}),
						...(options.skipNextFetch ? { skipNextFetch: true } : {}),
						// Scope the seeded conversation to a Rovo Space when applicable so the
						// nav can immediately render it under the correct space and exclude it
						// from the regular conversation list (see ChatListMenuItems filter).
						...(options.rovoSpaceId && fg('rovo_chat_spaces_fe')
							? { rovoSpaceId: options.rovoSpaceId }
							: {}),
					},
				}),
			);

			const chatInitiator = getChatInitiator();

			// Build synthetic ChatMessage[] from the seeded dialogues and pass directly to
			// processConversationMessages, which handles all boundary content extraction
			// (lumina, chart, expand, sculptor) consistently with fetchConversation —
			// without an extra getChat network call.
			const syntheticMessages: ChatMessage[] = options.dialogues.flatMap((dialogue) => {
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'sent',
					actionSubject: 'message',
					attributes: {
						messageId: humanMessageId,
						interactionSource: 'movedOver' as InteractionSource,
						followUpSource: options.followUpSource,
						timestamp: new Date().toISOString(),
						initiator: chatInitiator,
					},
				});
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'received',
					actionSubject: 'agentMessage',
					attributes: {
						messageId: assistantMessageId,
						humanMessageId,
						pluginsInvoked: [],
						hasSources: false,
						timestamp: new Date().toISOString(),
						initiator: chatInitiator,
					},
				});

				// When mimeType is 'text/adf', parse the ADF JSON string into a DocNode
				// so encodeAdf's isAdfContent check can pass it through natively.
				let agentContent: string | DocNode = dialogue.agent_message.content;
				if (dialogue.agent_message.mimeType === 'text/adf') {
					try {
						agentContent = JSON.parse(dialogue.agent_message.content);
					} catch {
						// If parsing fails, fall back to raw string content
					}
				}

				// Use raw ChatMessage types (not createStoreHuman/createStoreAssistant) so that
				// processConversationMessages can apply all boundary content extraction
				// (lumina, chart, expand, sculptor) before producing the final StoreChatItem[].
				const humanMessage: ChatMessage = {
					role: 'HUMAN',
					id: generateId(),
					content: dialogue.human_message.content,
					time_created: new Date().toISOString(),
					user_ari: 'unknown',
					plugin_invocations: [],
					isFromSeededConversation: true,
				};
				const assistantStreamResponse: StreamResponse = {
					type: 'FINAL_RESPONSE',
					message: {
						message: {
							role: 'ASSISTANT',
							id: generateId(),
							conversation_channel_id: createdConversation.id,
							content: agentContent,
							time_created: new Date().toISOString(),
							user_ari: 'unknown',
							plugin_invocations: [],
							author: {
								named_id: 'ai_mate_agent',
								name: 'unknown',
								actor_type: 'AGENT',
								ari: null,
								id: 'unknown',
							},
						},
					},
				};

				return [humanMessage, assistantStreamResponse];
			});

			const processedMessages = processConversationMessages({
				latestMessages: syntheticMessages,
				conversationId: createdConversation.id,
				sendAnalyticsEvent,
			});

			setMessages(createdConversation.id, processedMessages);

			dispatch(
				setSelectedConversation({
					conversationId: createdConversation.id,
				}),
			);

			if (options.mode && onNewConversationStartingWithSendMessage) {
				onNewConversationStartingWithSendMessage(createdConversation.id, options.mode);
			}

			experienceTracker.succeed({
				name: ExperienceName.CREATE_SEEDED_CONVERSATION_CHANNEL,
			});

			return createdConversation.id;
		},
	{
		experienceName: () => ExperienceName.CREATE_SEEDED_CONVERSATION_CHANNEL,
	},
);

/** Side-nav list items may attach UI-only fields; the chat store `Conversation` type must not persist them. */
const conversationMetadataForStore = (
	conversation: Conversation & { isEmpty?: unknown; currentTask?: unknown },
): Conversation => {
	const { isEmpty: _isEmpty, currentTask: _currentTask, ...storeConversation } = conversation;
	return storeConversation;
};

const setSelectedConversation = createSafeAction(
	StoreActionLoggableName.setSelectedConversation,
	({
		conversationId,
		sourceAgentId,
		conversationMetadata,
		targetMessageId,
	}: {
		conversationId: string;
		sourceAgentId?: string;
		/**
		 * When the id is not yet in `conversations` (e.g. opened from space-scoped GET list),
		 * seed the store from this payload so selection is not downgraded to empty chat.
		 */
		conversationMetadata?: Conversation & { isEmpty?: unknown; currentTask?: unknown };
		/**
		 * When set, ChatHistory will scroll to this specific message after the conversation loads.
		 * Used by Go to Chat from the Tasks List to route to the task's completion/current message.
		 */
		targetMessageId?: string | null;
	}): ChatStoreAction =>
		({ setState, getState, dispatch }, { siteId }) => {
			let resolvedConversationId = conversationId;

			if (!getState().conversations[conversationId]) {
				if (conversationMetadata?.id === conversationId) {
					dispatch(
						initializeConversationData({
							conversationId,
							data: {
								...conversationMetadataForStore(conversationMetadata),
								...(targetMessageId ? { targetTaskMessageId: targetMessageId } : {}),
							},
						}),
					);
				} else {
					resolvedConversationId = EMPTY_CONVERSATION_ID;
				}
			} else if (targetMessageId) {
				dispatch(
					setConversationData({
						conversationId,
						data: { targetTaskMessageId: targetMessageId },
					}),
				);
			}

			setState({
				currentConversationId: resolvedConversationId,
			});

			// Select the agent if applicable
			if (sourceAgentId) {
				dispatch(
					setConversationData({
						conversationId: resolvedConversationId,
						data: { sourceAgentId },
					}),
				);
			}

			// Store preference
			localStorage?.setItem(getChatLoadPreferenceKey(siteId), resolvedConversationId);
		},
);

const updateActionInvocationStatus = createSafeAction(
	StoreActionLoggableName.updateActionInvocationStatus,
	({
		conversationId,
		messageId,
		invocationId,
		status,
	}: {
		conversationId: string;
		messageId: string;
		invocationId: string;
		status: MessageActionInvocationStatus;
	}): ChatStoreAction =>
		async ({ getState }, { getMessages, setMessages }) => {
			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return;
			}

			const messages = getMessages(conversationId);
			const agentMessage = getAgentMessageById(messages, messageId);

			if (!agentMessage.result) {
				return;
			}

			const updatedMessages = [...messages];

			updatedMessages[agentMessage.index] = {
				...agentMessage.result,
				invokedActions: {
					...agentMessage.result.invokedActions,
					[invocationId]: status,
				},
			};

			setMessages(conversationId, updatedMessages);
		},
);

const updateConfirmedToolsActionStatuses = createSafeAction(
	StoreActionLoggableName.updateActionInvocationStatus,
	({
		conversationId,
		messageId,
		tools,
		status,
	}: {
		conversationId: string;
		messageId: string;
		tools: RovoLabsConfirmedTools['tools'];
		status: MessageActionInvocationStatus | 'fromResolution';
	}): ChatStoreAction =>
		async ({ getState }, { getMessages, setMessages }) => {
			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return;
			}

			const messages = getMessages(conversationId);
			const agentMessage = getAgentMessageById(messages, messageId);

			if (!agentMessage.result) {
				return;
			}

			const updatedInvokedActions = { ...agentMessage.result.invokedActions };
			for (const tool of tools) {
				const resolvedStatus: MessageActionInvocationStatus =
					status === 'fromResolution'
						? tool.resolution?.type === 'cancelled'
							? 'cancelled'
							: 'finished'
						: status;
				updatedInvokedActions[tool.invocation_id] = resolvedStatus;
			}

			const updatedMessages = [...messages];
			updatedMessages[agentMessage.index] = {
				...agentMessage.result,
				invokedActions: updatedInvokedActions,
			};

			setMessages(conversationId, updatedMessages);
		},
);

const updateActionData = createSafeAction(
	StoreActionLoggableName.updateActionData,
	({
		conversationId,
		messageId,
		invocationId,
		data,
	}: {
		conversationId: string;
		messageId: string;
		invocationId: string;
		data: any;
	}): ChatStoreAction =>
		async ({ getState }, { getMessages, setMessages }) => {
			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return;
			}

			const messages = getMessages(conversationId);
			const agentMessage = getAgentMessageById(messages, messageId);

			if (!agentMessage.result) {
				return;
			}

			const updatedMessages = [...messages];

			agentMessage.result.content.message.message.actions?.forEach((action) => {
				if (action.invocationId === invocationId) {
					action.data = {
						...(action.data as any),
						...data,
					};
				}
			});

			updatedMessages[agentMessage.index] = agentMessage.result;

			setMessages(conversationId, updatedMessages);
		},
);

const updateConversation =
	({
		conversationId,
		data,
	}: {
		conversationId: string;
		data: Partial<Pick<Conversation, 'name' | 'readStatus'>>;
	}): ChatStoreAction =>
	async ({ dispatch }, { assistanceService, experienceTracker }) => {
		experienceTracker.start({
			name: ExperienceName.UPDATE_CONVERSATION_CHANNEL,
			id: conversationId,
			attributes: {
				conversationId,
			},
		});

		try {
			await assistanceService?.updateConversationChannel(conversationId, data);
		} catch (error: any) {
			experienceTracker.fail({
				name: ExperienceName.UPDATE_CONVERSATION_CHANNEL,
				error,
			});

			throw error;
		}

		dispatch(
			setConversationData({
				conversationId,
				data,
			}),
		);

		experienceTracker.succeed({
			name: ExperienceName.UPDATE_CONVERSATION_CHANNEL,
		});
	};

type ResolveConversationActionArgs<TActionResult = any> = {
	conversationId: string;
	messageId: string;
	payload: MessageActionPayload;
	/** generic processor function for each action type that needs to return a response such as Jira issue creation */
	actionContentProcessor?: (message: AssistantMessage) => TActionResult;
	skipSuccessOptimisticAssistantResponse?: boolean;
	/** Skip tracking experience when called from bulk action (to avoid noise from individual failures) */
	skipExperienceTracking?: boolean;
	onError?: OnErrorCallback<ResolveConversationActionArgs<TActionResult>>;
};

const resolveConversationAction = createSafeAction<
	(args: ResolveConversationActionArgs) => any,
	undefined
>(
	StoreActionLoggableName.resolveConversationAction,
	({
		conversationId,
		messageId,
		payload,
		actionContentProcessor,
		skipSuccessOptimisticAssistantResponse = false,
		skipExperienceTracking = false,
	}) =>
		async <TActionResult>(
			{ dispatch, getState }: StoreActionApi<ChatStorePrivateState>,
			{
				assistanceService,
				experienceTracker,
				trackAIMAUEvent,
				sendAnalyticsEvent,
				getMessages,
				broadcastConfirmedActions,
				chatContextStoreActions,
			}: ChatStoreContainerProps,
		): Promise<TActionResult | undefined> => {
			let actionCallbackData: TActionResult | undefined;
			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return;
			}

			const humanMessageId = getAssociatedHumanMessageId(getMessages(conversationId), messageId);
			const featureArea = chatContextStoreActions.getIsStagingArea()
				? 'aifc-staging-area'
				: 'unknown';

			const attributes = {
				conversationId,
				resolution: payload.context.resolution,
				actionKey: payload.context.key,
			};

			if (!skipExperienceTracking) {
				experienceTracker.start({
					name: ExperienceName.RESOLVE_CONVERSATION_ACTION,
					id: conversationId,
					attributes,
				});
			}

			try {
				const { invocationId } = payload.context;

				// Set actions loading state
				dispatch(
					updateActionInvocationStatus({
						conversationId,
						messageId,
						invocationId,
						status: 'started',
					}),
				);

				const response = await assistanceService.resolveConversationAction(conversationId, payload);
				const { message } = response;

				dispatch(
					updateActionData({
						conversationId,
						messageId,
						invocationId,
						data: payload.context.data,
					}),
				);

				const isMessageWithContent = !!message.content;

				// Display a new agent message from the response to reflect the change in chat history
				if (
					isAssistantMessage(message) &&
					(isMessageWithContent || isActionExecutedSuccessfully(message))
				) {
					if (isMessageWithContent && !skipSuccessOptimisticAssistantResponse) {
						dispatch(
							addAssistantMessage({
								agentNamedId: message.author?.named_id,
								content: message.content,
								conversationId,
								id: message.id,
							}),
						);
					}

					actionCallbackData = actionContentProcessor?.(message) ?? undefined;

					dispatch(
						updateActionInvocationStatus({
							conversationId,
							messageId,
							invocationId,
							status: deriveInvocationStatus(message, payload.context),
						}),
					);

					if (isActionExecutedSuccessfully(message)) {
						broadcastConfirmedActions?.(conversationId, [payload.context]);
					}

					sendAnalyticsEvent({
						eventType: 'ui',
						action: 'success',
						actionSubject: 'rovoChatMessageAction',
						attributes: {
							recipientAgentNamedId: payload.context.recipient_agent_named_id,
							actionKey: payload.context.key,
							resolutionType: payload.context.resolution.type,
							featureArea,
							humanMessageId,
							messageId,
						},
					});
				} else {
					dispatch(
						updateActionInvocationStatus({
							conversationId,
							messageId,
							invocationId: payload.context.invocationId,
							status: 'errored',
						}),
					);

					sendAnalyticsEvent({
						eventType: 'track',
						action: 'error',
						actionSubject: 'rovoChatMessageAction',
						attributes: {
							recipientAgentNamedId: payload.context.recipient_agent_named_id,
							actionKey: payload.context.key,
							resolutionType: payload.context.resolution.type,
							featureArea,
							humanMessageId,
							messageId,
						},
					});
				}
			} catch (error: any) {
				if (!skipExperienceTracking) {
					experienceTracker.fail({
						name: ExperienceName.RESOLVE_CONVERSATION_ACTION,
						error,
						attributes,
					});
				}

				trackAIMAUEvent({
					type: 'error',
					userGenerated: true,
					source: 'messageActionsResolver',
					subjectId: payload.context.resolution.type,
					singleInstrumentationID: messageId,
				});

				sendAnalyticsEvent({
					eventType: 'track',
					action: 'error',
					actionSubject: 'rovoChatMessageAction',
					attributes: {
						recipientAgentNamedId: payload.context.recipient_agent_named_id,
						actionKey: payload.context.key,
						resolutionType: payload.context.resolution.type,
						featureArea,
						humanMessageId,
						messageId,
					},
				});

				dispatch(
					updateActionInvocationStatus({
						conversationId,
						messageId,
						invocationId: payload.context.invocationId,
						status: 'errored',
					}),
				);

				throw new MessageActionError({
					message: `Message action error: ${error.message} for actionKey: ${payload.context.key}`,
					conversationId,
					messageId: messageId,
					invocationId: payload.context.invocationId,
					actionKey: payload.context.key,
					cause: error,
				});
			}

			if (!skipExperienceTracking) {
				experienceTracker.succeed({
					name: ExperienceName.RESOLVE_CONVERSATION_ACTION,
					attributes,
				});
			}
			return actionCallbackData;
		},
	{
		actionErrorHandler: (context) => {
			const { onError, ...args } = context.args?.[0] ?? {};
			return onError ? onError({ ...context, args }) : { proceedToDefaultHandling: true };
		},
	},
);

type BulkResolveConversationActionsArgs<TActionResult = any> = {
	conversationId: string;
	messageId: string;
	payloads: MessageActionPayload[];
	/** generic processor function for each action type that needs to return a response such as Jira issue creation */
	actionContentProcessor?: (message: AssistantMessage) => TActionResult;
	skipSuccessOptimisticAssistantResponse?: boolean;
};

const bulkResolveConversationActions = createSafeAction<
	(args: BulkResolveConversationActionsArgs) => any,
	undefined
>(
	StoreActionLoggableName.bulkResolveConversationActions,
	({
		conversationId,
		messageId,
		payloads,
		actionContentProcessor,
		skipSuccessOptimisticAssistantResponse = false,
	}) =>
		async <TActionResult>(
			{ dispatch, getState }: StoreActionApi<ChatStorePrivateState>,
			{ experienceTracker }: ChatStoreContainerProps,
		): Promise<(TActionResult | undefined)[]> => {
			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return [];
			}

			experienceTracker.start({
				name: ExperienceName.BULK_RESOLVE_CONVERSATION_ACTIONS,
				id: conversationId,
				attributes: {
					conversationId,
					actionCount: payloads.length,
				},
			});

			// Execute all actions sequentially
			const actionCallbackResults: (TActionResult | undefined)[] = [];
			const successfulActions: MessageActionPayload['context'][] = [];
			let hasErrors = false;
			let successCount = 0;

			for (const payload of payloads) {
				const result = await dispatch(
					resolveConversationAction({
						conversationId,
						messageId,
						payload,
						actionContentProcessor,
						skipSuccessOptimisticAssistantResponse,
						skipExperienceTracking: true,
					}),
				);

				actionCallbackResults.push(result);

				// Track success: result is defined means action succeeded
				if (result !== undefined) {
					successCount++;
					successfulActions.push(payload.context);
				} else if (!actionContentProcessor) {
					// Action with no processor returns undefined on success
					successCount++;
				} else {
					// Action with processor returned undefined = failure
					hasErrors = true;
				}
			}

			if (hasErrors) {
				experienceTracker.fail({
					name: ExperienceName.BULK_RESOLVE_CONVERSATION_ACTIONS,
					error: new Error('Some bulk actions failed'),
					attributes: {
						conversationId,
						actionCount: payloads.length,
						successCount,
						failureCount: payloads.length - successCount,
					},
				});
			} else {
				experienceTracker.succeed({
					name: ExperienceName.BULK_RESOLVE_CONVERSATION_ACTIONS,
					attributes: {
						conversationId,
						actionCount: payloads.length,
					},
				});
			}

			return actionCallbackResults;
		},
);

type BulkResolveConversationUIActionsArgs<TActionResult = any> = {
	conversationId: string;
	messageId: string;
	payloads: MessageActionPayload[];
	/** generic processor function for each action type that needs to return a response such as Jira issue creation */
	actionContentProcessor?: (message: AssistantMessage) => TActionResult;
	skipSuccessOptimisticAssistantResponse?: boolean;
};

const bulkResolveConversationUIActions = createSafeAction<
	(args: BulkResolveConversationUIActionsArgs) => any,
	undefined
>(
	StoreActionLoggableName.bulkResolveConversationUIActions,
	({
		conversationId,
		messageId,
		payloads,
		actionContentProcessor,
		skipSuccessOptimisticAssistantResponse = false,
	}) =>
		async <TActionResult>(
			{ dispatch, getState }: StoreActionApi<ChatStorePrivateState>,
			{
				assistanceService,
				experienceTracker,
				sendAnalyticsEvent,
				getMessages,
				broadcastConfirmedActions,
				chatContextStoreActions,
			}: ChatStoreContainerProps,
		): Promise<(TActionResult | undefined)[]> => {
			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return [];
			}

			const humanMessageId = getAssociatedHumanMessageId(getMessages(conversationId), messageId);
			const featureArea = chatContextStoreActions.getIsStagingArea()
				? 'aifc-staging-area'
				: 'unknown';

			experienceTracker.start({
				name: ExperienceName.BULK_RESOLVE_CONVERSATION_ACTIONS,
				id: conversationId,
				attributes: {
					conversationId,
					actionCount: payloads.length,
				},
			});

			// Set all invocation statuses to 'started'
			for (const payload of payloads) {
				dispatch(
					updateActionInvocationStatus({
						conversationId,
						messageId,
						invocationId: payload.context.invocationId,
						status: 'started',
					}),
				);
			}

			try {
				const responses = await assistanceService.bulkResolveConversationUIActions(
					conversationId,
					messageId,
					{ items: payloads },
				);

				const actionCallbackResults: (TActionResult | undefined)[] = [];
				const successfulActions: MessageActionPayload['context'][] = [];
				let hasErrors = false;
				let successCount = 0;

				// Process each response paired with its corresponding payload
				for (let i = 0; i < payloads.length; i++) {
					const payload = payloads[i];
					const response = responses[i];
					const { invocationId } = payload.context;

					if (!response) {
						// No response for this item - mark as errored
						dispatch(
							updateActionInvocationStatus({
								conversationId,
								messageId,
								invocationId,
								status: 'errored',
							}),
						);
						hasErrors = true;
						actionCallbackResults.push(undefined);

						sendAnalyticsEvent({
							eventType: 'track',
							action: 'error',
							actionSubject: 'rovoChatMessageAction',
							attributes: {
								recipientAgentNamedId: payload.context.recipient_agent_named_id,
								actionKey: payload.context.key,
								resolutionType: payload.context.resolution.type,
								featureArea,
								humanMessageId,
								messageId,
							},
						});
						continue;
					}

					const { message } = response;

					dispatch(
						updateActionData({
							conversationId,
							messageId,
							invocationId,
							data: payload.context.data,
						}),
					);

					const isMessageWithContent = !!message.content;

					if (
						isAssistantMessage(message) &&
						(isMessageWithContent || isActionExecutedSuccessfully(message))
					) {
						if (isMessageWithContent && !skipSuccessOptimisticAssistantResponse) {
							dispatch(
								addAssistantMessage({
									agentNamedId: message.author?.named_id,
									content: message.content,
									conversationId,
									id: message.id,
								}),
							);
						}

						const callbackData = actionContentProcessor?.(message) ?? undefined;
						actionCallbackResults.push(callbackData);

						dispatch(
							updateActionInvocationStatus({
								conversationId,
								messageId,
								invocationId,
								status: deriveInvocationStatus(message, payload.context),
							}),
						);

						if (isActionExecutedSuccessfully(message)) {
							successfulActions.push(payload.context);
						}

						successCount++;

						sendAnalyticsEvent({
							eventType: 'ui',
							action: 'success',
							actionSubject: 'rovoChatMessageAction',
							attributes: {
								recipientAgentNamedId: payload.context.recipient_agent_named_id,
								actionKey: payload.context.key,
								resolutionType: payload.context.resolution.type,
								featureArea,
								humanMessageId,
								messageId,
							},
						});
					} else {
						dispatch(
							updateActionInvocationStatus({
								conversationId,
								messageId,
								invocationId,
								status: 'errored',
							}),
						);
						hasErrors = true;
						actionCallbackResults.push(undefined);

						sendAnalyticsEvent({
							eventType: 'track',
							action: 'error',
							actionSubject: 'rovoChatMessageAction',
							attributes: {
								recipientAgentNamedId: payload.context.recipient_agent_named_id,
								actionKey: payload.context.key,
								resolutionType: payload.context.resolution.type,
								featureArea,
								humanMessageId,
								messageId,
							},
						});
					}
				}

				// Broadcast all successful confirmed actions at once
				if (successfulActions.length > 0) {
					broadcastConfirmedActions?.(conversationId, successfulActions);
				}

				if (hasErrors) {
					experienceTracker.fail({
						name: ExperienceName.BULK_RESOLVE_CONVERSATION_ACTIONS,
						error: new Error('Some bulk actions failed'),
						attributes: {
							conversationId,
							actionCount: payloads.length,
							successCount,
							failureCount: payloads.length - successCount,
						},
					});
				} else {
					experienceTracker.succeed({
						name: ExperienceName.BULK_RESOLVE_CONVERSATION_ACTIONS,
						attributes: {
							conversationId,
							actionCount: payloads.length,
						},
					});
				}

				return actionCallbackResults;
			} catch (error: any) {
				// If the entire bulk request fails, mark all invocations as errored
				for (const payload of payloads) {
					dispatch(
						updateActionInvocationStatus({
							conversationId,
							messageId,
							invocationId: payload.context.invocationId,
							status: 'errored',
						}),
					);
				}

				experienceTracker.fail({
					name: ExperienceName.BULK_RESOLVE_CONVERSATION_ACTIONS,
					error: error instanceof Error ? error : new Error('Bulk resolve failed'),
					attributes: {
						conversationId,
						actionCount: payloads.length,
						successCount: 0,
						failureCount: payloads.length,
					},
				});

				throw error;
			}
		},
);

const deleteConversation =
	({ conversationId }: { conversationId: string }): ChatStoreAction =>
	async ({ dispatch, getState }, { assistanceService, experienceTracker }) => {
		experienceTracker.start({
			name: ExperienceName.CLEAR_CHAT,
			id: conversationId,
			attributes: {
				conversationId,
			},
		});

		await getState().conversations[conversationId]?.storeAbortController?.abort();

		try {
			await assistanceService.deleteChat(conversationId);
		} catch (error: any) {
			experienceTracker.fail({
				name: ExperienceName.CLEAR_CHAT,
				error,
			});

			throw error;
		}

		FeatureGates.getExperimentValue(
			'rovo_chat_perf_init_load_optimization',
			'isBackgroundConversationsFetchEnabled',
			false,
		)
			? await dispatch(fetchConversationChannelsData())
			: await dispatch(initConversationChannelsData());

		experienceTracker.succeed({
			name: ExperienceName.CLEAR_CHAT,
		});
	};

/**
 * ************************************************************
 * Store
 * ************************************************************
 */

type WarmConversationCacheArgs = {
	conversation: {
		id: string;
		name?: string;
		timeCreated?: string;
		agentId?: string;
	};
	forceRefresh?: boolean;
	onSuccess?: () => void;
	onError?: (error: Error) => void;
};

const warmConversationCache = createSafeAction(
	StoreActionLoggableName.warmConversationCache,
	({
		conversation: conversationParams,
		forceRefresh,
		onSuccess,
		onError,
	}: WarmConversationCacheArgs): ChatStoreAction =>
		async (
			{ getState, dispatch },
			{ assistanceService, experienceTracker, sendAnalyticsEvent, setMessages },
		) => {
			const conversationId = conversationParams.id;

			const existingConversation = getState().conversations[conversationId];

			// Skip if conversation is actively in-flight
			if (
				existingConversation?.status === CHAT_STORE_STATUS.loadingChat ||
				existingConversation?.status === CHAT_STORE_STATUS.awaitingResponse ||
				existingConversation?.status === CHAT_STORE_STATUS.typingResponse
			) {
				return;
			}

			// Skip if conversation is already loaded (idle with messages) unless forceRefresh
			if (existingConversation?.status === CHAT_STORE_STATUS.idle && !forceRefresh) {
				return;
			}

			try {
				experienceTracker.start({
					name: ExperienceName.WARM_CONVERSATION_CACHE,
					id: conversationId,
					attributes: {
						conversationId,
						forceRefresh: Boolean(forceRefresh),
					},
				});

				// Bootstrap conversation in store with provided metadata or client defaults
				dispatch(
					initializeConversationData({
						conversationId,
						data: {
							id: conversationId,
							name: conversationParams.name ?? existingConversation?.name ?? '',
							timeCreated:
								conversationParams.timeCreated ??
								existingConversation?.timeCreated ??
								new Date().toISOString(),
							status: CHAT_STORE_STATUS.loadingChat,
							traces: [],
							sourceAgentId:
								conversationParams.agentId ?? existingConversation?.sourceAgentId ?? '',
							messages: [],
						},
					}),
				);

				// If metadata params not fully provided, fetch from API in parallel (silent failure)
				const hasFullMetadata =
					conversationParams.name != null && conversationParams.timeCreated != null;

				const metadataPromise: Promise<void> = hasFullMetadata
					? Promise.resolve()
					: assistanceService
							.fetchConversationMetadata(conversationId)
							.then((metadata) => {
								dispatch(
									setConversationData({
										conversationId,
										data: {
											name: metadata.name ?? conversationParams.name ?? '',
											timeCreated:
												metadata.time_created ??
												conversationParams.timeCreated ??
												existingConversation?.timeCreated ??
												new Date().toISOString(),
											sourceAgentId:
												metadata.agent?.agent_named_id ??
												conversationParams.agentId ??
												existingConversation?.sourceAgentId ??
												'',
											...(metadata.tags !== undefined ? { tags: metadata.tags } : {}),
										},
									}),
								);
							})
							.catch(() => {
								// Silent failure — keep client defaults
							});

				// Run messages fetch and metadata fetch in parallel; wait for both
				const chatPromise = assistanceService.getChat(conversationId);

				const [chatResult] = await Promise.allSettled([chatPromise, metadataPromise]);

				if (chatResult.status === 'rejected') {
					const normalizedError =
						chatResult.reason instanceof Error
							? chatResult.reason
							: new Error(String(chatResult.reason));
					experienceTracker.fail({
						name: ExperienceName.WARM_CONVERSATION_CACHE,
						error: normalizedError,
					});
					onError?.(normalizedError);
					return;
				}

				const serviceChat = chatResult.value;

				const status = getState().conversations[conversationId]?.status;

				if (status !== CHAT_STORE_STATUS.loadingChat) {
					return;
				}

				const messages = processConversationMessages({
					latestMessages: serviceChat,
					conversationId,
					sendAnalyticsEvent,
				});

				const fetchedAgentVersion = extractAgentVersionFromMessages(serviceChat);

				dispatch(
					setConversationData({
						conversationId,
						data: {
							...clearStatus({ lastAgentMessageStatus: 'SUCCESSFUL' }),
							...(fetchedAgentVersion ? { agentVersion: fetchedAgentVersion } : {}),
						},
					}),
				);

				setMessages(conversationId, messages);

				experienceTracker.succeed({
					name: ExperienceName.WARM_CONVERSATION_CACHE,
				});

				onSuccess?.();
			} catch (e) {
				const normalizedError = e instanceof Error ? e : new Error(String(e));
				experienceTracker.fail({
					name: ExperienceName.WARM_CONVERSATION_CACHE,
					error: normalizedError,
				});
				onError?.(normalizedError);
			}
		},
	{
		experienceName: () => ExperienceName.WARM_CONVERSATION_CACHE,
		actionErrorHandler: () => {
			// Suppress all default error handling for cache warming
			return;
		},
	},
);

/**
 * Seeds a conversation into the store without going through createSafeAction.
 *
 * This is used by the template-smartCreate flow where createConversationChannel
 * has already created a real backend conversation. We need to:
 *   1. Register it in state.conversations (so setSelectedConversation can find it)
 *   2. Set it as the active conversation
 *
 * Unlike createConversationOptimistically, this does NOT use createSafeAction,
 * so it works even when ChatStoreContainer is not mounted (e.g. template gallery page).
 */
const seedConversation =
	({
		conversationId,
		skipNextFetch,
	}: {
		conversationId: string;
		/** When true, ChatStoreContainer will skip its initial fetchConversation call.
		 * Use this when messages have already been populated locally (e.g. template flow)
		 * to prevent the fetch from overwriting locally-seeded messages. */
		skipNextFetch?: boolean;
	}): ChatStoreAction =>
	({ getState, setState }) => {
		const time = new Date().toISOString();
		// Register in conversations map
		setState({
			conversations: {
				...getState().conversations,
				[conversationId]: {
					...(getState().conversations[conversationId] ?? {}),
					id: conversationId,
					name: generateConversationName(),
					status: CHAT_STORE_STATUS.idle,
					timeCreated: time,
					lastMessageTimestamp: time,
					traces: [],
					messages: [],
					lastMessageType: [],
					...(skipNextFetch ? { skipNextFetch: true } : {}),
				},
			},
			// Also set as active conversation
			currentConversationId: conversationId,
		});
	};

const clearTaskInProgress =
	({ conversationId }: { conversationId: string }): ChatStoreAction =>
	({ getState, dispatch }) => {
		const conversation = getState().conversations[conversationId];
		const tasks = conversation?.tasks;
		if (!tasks || tasks.length === 0) {
			return;
		}
		const updatedTasks = tasks.filter((_task, index) => index !== tasks.length - 1);
		dispatch(
			setConversationData({
				conversationId,
				data: { tasks: updatedTasks.length > 0 ? updatedTasks : undefined },
			}),
		);
	};

const setTaskProgressState =
	({
		conversationId,
		progressState,
		title,
		seekingCompletedAck,
		messageId,
		completionMessageId,
		updatedAt,
		taskId,
	}: {
		conversationId: string;
		progressState: TaskProgressState;
		title?: string;
		seekingCompletedAck?: boolean;
		messageId?: string;
		completionMessageId?: string;
		updatedAt?: string;
		taskId?: string;
	}): ChatStoreAction =>
	({ getState, dispatch }, { setReasoningMode }) => {
		const conversation = getState().conversations[conversationId];
		const tasks = conversation?.tasks;

		// If no tasks exist, create a new task
		if (!tasks || tasks.length === 0) {
			const newTask = {
				id: taskId ?? `task-${Date.now()}`,
				// Only set `taskId` when we have the real BE task_id;
				...(taskId ? { taskId } : {}),
				progressState,
				title: title ?? '',
				...(seekingCompletedAck !== undefined ? { seekingCompletedAck } : {}),
				...(messageId ? { messageId } : {}),
				...(completionMessageId ? { completionMessageId } : {}),
				...(updatedAt ? { updatedAt } : {}),
			};
			dispatch(
				setConversationData({
					conversationId,
					data: { tasks: [newTask] },
				}),
			);
			return;
		}

		const updatedTasks = [...tasks];
		const lastTask = updatedTasks[updatedTasks.length - 1];

		// Skip updates if the topmost task is already in a terminal state
		if (isTerminalTaskState(lastTask.progressState)) {
			return;
		}

		updatedTasks[updatedTasks.length - 1] = {
			...lastTask,
			progressState,
			...(title !== undefined ? { title } : {}),
			...(seekingCompletedAck !== undefined ? { seekingCompletedAck } : {}),
			...(messageId ? { messageId } : {}),
			...(completionMessageId ? { completionMessageId } : {}),
			...(updatedAt ? { updatedAt } : {}),
		};
		dispatch(
			setConversationData({
				conversationId,
				data: { tasks: updatedTasks },
			}),
		);

		// Auto-reset reasoning mode when task reaches a terminal state
		if (isTerminalTaskState(progressState)) {
			setReasoningMode(conversationId, 'auto');
		}
	};

/**
 * Creates a new task object and appends it to the conversation's tasks array.
 * Only called when a DRAFT status stream message is received.
 */
const createTaskFromStream =
	({
		conversationId,
		taskId,
		title,
		updatedAt,
		messageId,
	}: {
		conversationId: string;
		taskId: string;
		title: string;
		updatedAt?: string;
		messageId?: string;
	}): ChatStoreAction =>
	({ getState, dispatch }) => {
		const conversation = getState().conversations[conversationId];
		const tasks = conversation?.tasks ?? [];

		// Idempotency guard — skip if a task with this taskId already exists.
		// This prevents duplicate entries when TASK_STATUS:DRAFT is replayed
		// during reconnect or retransmitted by the server.
		if (tasks.some((t) => t.taskId === taskId)) {
			return;
		}

		const newTask = {
			id: taskId,
			taskId,
			progressState: 'Draft' as TaskProgressState,
			title,
			...(messageId ? { messageId } : {}),
			...(updatedAt ? { updatedAt } : {}),
		};
		dispatch(
			setConversationData({
				conversationId,
				data: { tasks: [...tasks, newTask] },
			}),
		);
	};

const setTaskTodoSnapshot =
	({
		conversationId,
		todoSnapshot,
	}: {
		conversationId: string;
		todoSnapshot: TodoSnapshot;
	}): ChatStoreAction =>
	({ getState, dispatch }) => {
		const conversation = getState().conversations[conversationId];
		const tasks = conversation?.tasks;

		if (!tasks || tasks.length === 0) {
			return;
		}

		const updatedTasks = [...tasks];
		const lastTask = updatedTasks[updatedTasks.length - 1];

		// Don't update todo snapshot if task is already terminal
		if (isTerminalTaskState(lastTask.progressState)) {
			return;
		}

		updatedTasks[updatedTasks.length - 1] = {
			...lastTask,
			todoSnapshot,
		};
		dispatch(
			setConversationData({
				conversationId,
				data: { tasks: updatedTasks },
			}),
		);
	};

/**
 * Stops the current task for a conversation.
 * If a stream is active (storeAbortController exists), delegates to abortRequests which will
 * abort the stream and set the task to Cancelled.
 * If no stream is active, directly sets the task progress state to Cancelled.
 */
const stopTask = createSafeAction(
	StoreActionLoggableName.stopTask,
	({
		conversationId,
	}: {
		conversationId: string;
	}): ChatStoreAction =>
		({ getState, dispatch }, { assistanceService }) => {
			const conversation = getState().conversations[conversationId];

			if (conversation?.storeAbortController) {
				// Stream is active — delegate to abortRequests which handles abort + cancellation
				dispatch(abortRequests({ conversationId, taskModeEnabled: true }));
			} else {
				// No active stream — cancel the task directly
				const tasks = conversation?.tasks;
				const lastTask = tasks && tasks.length > 0 ? tasks[tasks.length - 1] : undefined;

				dispatch(
					setTaskProgressState({
						conversationId,
						progressState: 'Cancelled',
					}),
				);

				// Fire-and-forget cancel request to backend
				if (lastTask?.taskId && !isTerminalTaskState(lastTask.progressState)) {
					assistanceService
						.cancelTask(lastTask.taskId)
						.catch((err: unknown) => {
							logException({
								name: 'rovoCancelTaskFromStopTask',
								exception: err,
								tags: { taskId: lastTask.taskId },
							});
						});
				}
			}
		},
);

const ackTaskCompleted =
	({ conversationId }: { conversationId: string }): ChatStoreAction =>
	({ getState, dispatch }) => {
		const conversation = getState().conversations[conversationId];
		const tasks = conversation?.tasks;
		if (!tasks || tasks.length === 0) {
			return;
		}
		const updatedTasks = [...tasks];
		const lastTask = updatedTasks[updatedTasks.length - 1];
		updatedTasks[updatedTasks.length - 1] = { ...lastTask, seekingCompletedAck: false };
		dispatch(
			setConversationData({
				conversationId,
				data: { tasks: updatedTasks },
			}),
		);
	};

const clearTargetTaskMessageId =
	({ conversationId }: { conversationId: string }): ChatStoreAction =>
	({ dispatch }) => {
		dispatch(setConversationData({ conversationId, data: { targetTaskMessageId: null } }));
	};

/**
 * Checks the currently selected conversation's tasks after conversations are loaded.
 * If the last task is not in a terminal state (Completed/Cancelled/Failed),
 * restores the chat mode to 'task' so the user resumes in task mode.
 */
const restoreTaskModeIfNeeded =
	(): ChatStoreAction =>
	({ getState }, { setReasoningMode }) => {
		const { currentConversationId, conversations } = getState();
		const conversation = conversations[currentConversationId];

		if (!conversation?.tasks || conversation.tasks.length === 0) {
			return;
		}

		const lastTask = conversation.tasks[conversation.tasks.length - 1];

		if (!isTerminalTaskState(lastTask.progressState)) {
			setReasoningMode(currentConversationId, 'task');
		}
	};

const actions = {
	abortRequests,
	addAgentMessage,
	addErrorMessage,
	answerNow,
	bulkResolveConversationActions,
	bulkResolveConversationUIActions,
	clearFollowUpMessages,
	ackTaskCompleted,
	clearTargetTaskMessageId,
	clearTaskInProgress,
	createTaskFromStream,
	setTaskProgressState,
	setTaskTodoSnapshot,
	stopTask,
	createConversationOptimistically,
	createSeededConversation,
	deleteConversation,
	getChat: fetchConversation,
	handleChatOpenPubsubAction,
	hydrateConversationWithExternalParams,
	initConversationChannelsData,
	fetchConversationChannelsData,
	setConversationData,
	removeMessage,
	resolveConversationAction,
	sendMessage,
	sendMessageConfirmedTools,
	setSelectedConversation,
	updateConversation,
	removeConversationFromStore,
	reconnectToInProgressStream,
	restoreTaskModeIfNeeded,
	warmConversationCache,
	seedConversation,
};

const chatStore = createChatStore(actions);

export type ChatStoreActions = typeof actions;

/**
 * ************************************************************
 * Container
 * ************************************************************
 */
const storeInit =
	(): ChatStoreAction =>
	async ({ dispatch }, { setChatStatus, skipAutoConversationSelection }) => {
		addSentryBreadcrumb('ChatStoreContainer: storeInit');
		setChatStatus('launching');

		const initConversationWithMetrics = async () => {
			interactionMetrics.initializeChannels.start();
			await dispatch(
				initConversationChannelsData({
					isInitialLoad: true,
					autoSelectConversation: !skipAutoConversationSelection,
				}),
			);
			interactionMetrics.initializeChannels.stop();
		};

		// Init conversation hydrates the chat store with conversation from URL or Storage
		// and fetches conversations list (however, fetching conversations list is performed "in the background")
		// conversations list is fetched to be later on displayed in the sidebar/conversation list to remove/minimize
		// visual delay when user opens the side-navigation; or in full-screen mode to fetch conversations as early as possible;
		const initConversation = async () => {
			dispatch(
				hydrateConversationWithExternalParams({
					autoSelectConversation: !skipAutoConversationSelection,
				}),
			);
			interactionMetrics.initializeChannels.start();
			await dispatch(fetchConversationChannelsData({ isInitialLoad: true }));
			interactionMetrics.initializeChannels.stop();
		};

		FeatureGates.getExperimentValue(
			'rovo_chat_perf_init_load_optimization',
			'isBackgroundConversationsFetchEnabled',
			false,
		)
			? initConversation()
			: initConversationWithMetrics();

		addSentryBreadcrumb('ChatStoreContainer: storeInit completed');
	};

const storeInitOnUpdate = (): ChatStoreAction => {
	return () => {};
};

const chatStoreContainerOptions = {
	onInit: storeInit,
	onUpdate: storeInitOnUpdate,
};

export const ChatStoreContainer = createContainer<
	ChatStorePrivateState,
	ChatStoreActions,
	ChatStoreContainerProps
>(chatStore, chatStoreContainerOptions);

/**
 * ************************************************************
 * Main Hook
 * ************************************************************
 */

const publicStateSelector = (state: ChatStorePrivateState): ChatStoreState => {
	return {
		...state,
		checkActiveConversationMatchesSource: (id?: string) => {
			return checkActiveConversationMatchesSource(state, id);
		},
	};
};

export const useChatStore = createHook(chatStore, {
	selector: publicStateSelector,
});

/**
 * This is the preferred hook if component does not need any state, as this will never trigger re-render
 */
export const useChatStoreActions = createActionsHook(chatStore);
